import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Autocomplete,
  Popover,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Psychology as PsychologyIcon,
  ContentCopy as CopyIcon,
  Clear as ClearIcon,
  Api as ApiIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  FileCopy as FileCopyIcon,
  PlayArrow as PlayArrowIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  PlaylistAdd as PlaylistAddIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  SkipNext as SkipNextIcon,
  RestartAlt as RestartAltIcon,
  Visibility as VisibilityIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  LibraryBooks as LibraryBooksIcon,
  Home as HomeIcon,
  Undo as UndoIcon,
  Storage as StorageIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import LlmService from '../services/LlmService';
import PromptService from '../services/PromptService';
import FlexService from '../services/FlexService';
import PromptLibraryPanel from './PromptLibraryPanel';
import TestRunRating from './TestRunRating';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';

// Tab types
const TAB_TYPES = {
  LLM: 'llm',
  API: 'api',
  TOOL: 'tool',
};

// Generate unique tab ID
let tabIdCounter = 0;
const generateTabId = () => `tab-${++tabIdCounter}`;

// ============================================================================
// LLM Tab Content Component
// ============================================================================
const LlmTabContent = ({ colors, tabState, setTabState }) => {
  const [availableModels, setAvailableModels] = useState([]);
  const [instructionOptions, setInstructionOptions] = useState([]);
  const [instructionLoading, setInstructionLoading] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const settingsOpen = Boolean(settingsAnchorEl);

  // Save dialog state
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({
    name: '',
    description: '',
    systemDomain: 'custom',
    functionalContext: 'playground',
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Copy dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyName, setCopyName] = useState('');
  const [copyLoading, setCopyLoading] = useState(false);
  const [copyError, setCopyError] = useState(null);

  // Simulate state (no dialog - settings are in the popover)
  const [simulateLoading, setSimulateLoading] = useState(false);

  // Debug mode state
  const [debugState, setDebugState] = useState({
    mode: 'IDLE', // IDLE, SIMULATED, TOOLS_DETECTED, EXECUTING, ALL_DONE, FINAL
    currentStep: 0,
    totalSteps: 0,
    sessionId: null,
    detectedTools: [], // [{toolKey, toolId, name, status: 'pending'|'current'|'done', response, apiEndpoint, parameters}]
    accumulatedResponses: [],
    wizardContext: {},
    originalLlmResponse: '',
    error: null,
  });
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);
  const [stepNextLoading, setStepNextLoading] = useState(false);
  const [toolParamEditing, setToolParamEditing] = useState(null); // toolKey being edited
  const [editedParams, setEditedParams] = useState({});
  const [allToolKeys, setAllToolKeys] = useState([]); // Cache of all tool keys for detection
  const [promptViewMode, setPromptViewMode] = useState('raw'); // 'raw' or 'processed'
  const [processedSystemPrompt, setProcessedSystemPrompt] = useState(null); // Processed prompt from simulation

  // Prompt Library state
  const [promptLibraryOpen, setPromptLibraryOpen] = useState(false);
  const [currentPromptId, setCurrentPromptId] = useState(null); // Currently loaded prompt from library
  const [currentTestRunId, setCurrentTestRunId] = useState(null); // Last test run ID for rating
  const [availableTools, setAvailableTools] = useState([]); // Tools for import dialog

  // Variable checking state
  const [variableCheckResult, setVariableCheckResult] = useState(null); // Result from check-variables API
  const [isCheckingVariables, setIsCheckingVariables] = useState(false);
  const [rawSystemPrompt, setRawSystemPrompt] = useState(null); // Original prompt before check (for revert)

  // Get selected model name for display
  const getSelectedModelName = () => {
    const model = availableModels.find(m => m.modelId === tabState.selectedModelId);
    return model ? (model.modelName ? model.modelName.split('/').pop() : model.modelId) : 'No model';
  };

  // State for variable hover popover
  const [variablePopover, setVariablePopover] = useState({ anchorEl: null, varInfo: null });

  // Handle variable hover
  const handleVariableHover = (event, varInfo) => {
    if (!varInfo?.resolved) {
      setVariablePopover({ anchorEl: event.currentTarget, varInfo });
    }
  };

  const handleVariableHoverClose = () => {
    setVariablePopover({ anchorEl: null, varInfo: null });
  };

  // Navigate to FLEX IDE to create a script for a missing variable
  const handleCreateScriptForVariable = (varInfo) => {
    if (!varInfo) return;

    // Extract the variable name (remove {{ and }})
    const varName = varInfo.name || varInfo.variable?.replace(/^\{\{|\}\}$/g, '') || 'new_variable';

    // Open FLEX IDE in new tab with createScript parameter
    window.open(`/flex-ide?createScript=${encodeURIComponent(varName)}`, '_blank');

    // Close the popover
    handleVariableHoverClose();
  };

  // Render text with {{varname}} patterns in bold
  // Uses variableCheckResult to determine if variables are resolved (green) or unresolved (red)
  // Unresolved variables show a hover menu with troubleshooting info
  const renderTextWithBoldVariables = (text, checkResult) => {
    if (!text) return null;

    // Pattern to match {{...}} variables
    const variablePattern = /(\{\{[^}]+\}\})/g;
    const parts = text.split(variablePattern);

    return parts.map((part, index) => {
      if (variablePattern.test(part)) {
        // Check if this variable was resolved
        const varInfo = checkResult?.variables?.find(v => v.fullMatch === part || v.variable === part);
        const isResolved = varInfo?.resolved === true;

        return (
          <span
            key={index}
            onMouseEnter={(e) => handleVariableHover(e, varInfo)}
            onMouseLeave={handleVariableHoverClose}
            style={{
              fontWeight: 'bold',
              color: isResolved ? colors.success : colors.error,
              backgroundColor: isResolved ? colors.successSubtle : colors.errorSubtle,
              padding: '0 2px',
              borderRadius: '2px',
              cursor: isResolved ? 'default' : 'help',
              textDecoration: isResolved ? 'none' : 'underline dotted',
            }}
          >
            {part}
          </span>
        );
      }
      // Reset lastIndex to prevent issues with global flag
      variablePattern.lastIndex = 0;
      return <span key={index}>{part}</span>;
    });
  };

  // Load all tool keys for detection and available tools for import
  useEffect(() => {
    const loadToolKeys = async () => {
      try {
        const data = await LlmService.getTools();
        const tools = data.tools || [];
        setAllToolKeys(tools.map(t => ({ toolKey: t.toolKey, toolId: t.toolId, name: t.toolName || t.toolKey })));
        setAvailableTools(tools); // Also store full tools for import dialog
      } catch (err) {
        console.error('Failed to load tool keys for detection:', err);
      }
    };
    loadToolKeys();
  }, []);

  // Handle selecting a prompt from the library
  const handleSelectPromptFromLibrary = (prompt) => {
    setTabState(prev => ({
      ...prev,
      systemPrompt: prompt.promptText,
    }));
    setCurrentPromptId(prompt.id);
    setProcessedSystemPrompt(null);
    setPromptViewMode('raw');
  };

  // Detect tools from LLM response text
  const detectToolsFromResponse = async (responseText) => {
    const detectedTools = [];
    const seenKeys = new Set();

    // 1. Try to parse JSON function calls
    const jsonPatterns = [
      /\{[^{}]*"function"\s*:\s*"([^"]+)"[^{}]*\}/g,
      /\{[^{}]*"tool"\s*:\s*"([^"]+)"[^{}]*\}/g,
      /\{[^{}]*"use_agent"\s*:\s*"([^"]+)"[^{}]*\}/g,
      /\{[^{}]*"agent"\s*:\s*"([^"]+)"[^{}]*\}/g,
    ];

    for (const pattern of jsonPatterns) {
      let match;
      while ((match = pattern.exec(responseText)) !== null) {
        const toolKey = match[1];
        if (!seenKeys.has(toolKey)) {
          seenKeys.add(toolKey);
          const toolInfo = allToolKeys.find(t => t.toolKey === toolKey);
          if (toolInfo) {
            detectedTools.push({
              toolKey: toolInfo.toolKey,
              toolId: toolInfo.toolId,
              name: toolInfo.name,
              status: 'pending',
              response: null,
              apiEndpoint: null,
              availableFunctions: null,
              parameters: {},
            });
          }
        }
      }
    }

    // 2. Check for text mentions of known tool keys
    for (const tool of allToolKeys) {
      if (!seenKeys.has(tool.toolKey)) {
        const regex = new RegExp(`\\b${tool.toolKey.replace(/_/g, '[_\\s]?')}\\b`, 'i');
        if (regex.test(responseText)) {
          seenKeys.add(tool.toolKey);
          detectedTools.push({
            toolKey: tool.toolKey,
            toolId: tool.toolId,
            name: tool.name,
            status: 'pending',
            response: null,
            apiEndpoint: null,
            availableFunctions: null,
            parameters: {},
          });
        }
      }
    }

    // 3. Load API endpoints for each detected tool
    for (const tool of detectedTools) {
      try {
        const toolData = await LlmService.getTool(tool.toolId);
        // Backend returns 'functions' (parsed) not 'availableFunctions' (raw)
        const funcs = toolData.functions || [];
        if (funcs.length > 0) {
          tool.availableFunctions = funcs;
          // Get first function with api_endpoint as default
          const funcWithEndpoint = funcs.find(f => f.api_endpoint);
          if (funcWithEndpoint) {
            tool.apiEndpoint = funcWithEndpoint.api_endpoint;
            tool.apiMethod = funcWithEndpoint.method || 'GET';
            tool.functionName = funcWithEndpoint.name;
          }
        }
        if (toolData.prompt) {
          tool.prompt = toolData.prompt;
        }
      } catch (err) {
        console.error(`Failed to load tool details for ${tool.toolKey}:`, err);
      }
    }

    return detectedTools;
  };

  // Load available models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const data = await LlmService.getLlmModels();
        // Filter out models without valid modelId and modelName
        const models = (data.models || []).filter(m => m && m.modelId && m.modelName);
        setAvailableModels(models);
        if (!tabState.selectedModelId && models.length > 0) {
          setTabState(prev => ({ ...prev, selectedModelId: models[0].modelId }));
        }
      } catch (err) {
        console.error('Failed to load models:', err);
        const fallbackModels = [
          { modelId: '1', modelName: 'meta-llama/Llama-3.3-70B-Instruct-Turbo' },
        ];
        setAvailableModels(fallbackModels);
        if (!tabState.selectedModelId) {
          setTabState(prev => ({ ...prev, selectedModelId: fallbackModels[0].modelId }));
        }
      }
    };
    loadModels();
  }, []);

  // Search system instructions as user types
  const handleInstructionSearch = async (event, value, reason) => {
    // Load all instructions on open/clear, or search when typing
    if (reason === 'reset' || reason === 'clear') {
      return;
    }
    setInstructionLoading(true);
    try {
      const data = await LlmService.searchSystemInstructions(value || '');
      setInstructionOptions(data.instructions || []);
    } catch (err) {
      console.error('Failed to search instructions:', err);
      setInstructionOptions([]);
    } finally {
      setInstructionLoading(false);
    }
  };

  // Load instructions when dropdown opens
  const handleInstructionOpen = async () => {
    if (instructionOptions.length === 0) {
      setInstructionLoading(true);
      try {
        const data = await LlmService.searchSystemInstructions('');
        setInstructionOptions(data.instructions || []);
      } catch (err) {
        console.error('Failed to load instructions on open:', err);
        setInstructionOptions([]);
      } finally {
        setInstructionLoading(false);
      }
    }
  };

  // Load full system instruction when selected
  const handleInstructionSelect = async (event, value) => {
    setSelectedInstruction(value);
    if (value && value.id) {
      try {
        const data = await LlmService.getSystemInstruction(value.id);
        setTabState(prev => ({ ...prev, systemPrompt: data.systemInstruction || '' }));
      } catch (err) {
        console.error('Failed to load instruction:', err);
      }
    }
  };

  const handleSend = async () => {
    if (!tabState.userPrompt?.trim()) {
      setTabState(prev => ({ ...prev, error: 'User prompt is required' }));
      return;
    }
    if (!tabState.selectedModelId) {
      setTabState(prev => ({ ...prev, error: 'Please select a model' }));
      return;
    }

    setTabState(prev => ({ ...prev, isLoading: true, error: null, response: null }));

    try {
      const result = await LlmService.sendToLlmPlayground({
        systemPrompt: tabState.systemPrompt?.trim() || undefined,
        userPrompt: tabState.userPrompt.trim(),
        modelId: tabState.selectedModelId,
        temperature: tabState.temperature,
        maxTokens: tabState.maxTokens,
      });
      setTabState(prev => ({ ...prev, response: result, isLoading: false }));
    } catch (err) {
      setTabState(prev => ({ ...prev, error: err.message || 'Failed to get response', isLoading: false }));
    }
  };

  const handleClear = () => {
    setSelectedInstruction(null);
    setInstructionOptions([]);
    setProcessedSystemPrompt(null);
    setPromptViewMode('raw');
    setTabState(prev => ({
      ...prev,
      systemPrompt: '',
      userPrompt: '',
      response: null,
      error: null,
    }));
  };

  const handleCopyResponse = () => {
    if (tabState.response?.response) {
      navigator.clipboard.writeText(tabState.response.response);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleOpenSaveDialog = () => {
    setSaveForm({
      name: '',
      description: '',
      systemDomain: 'custom',
      functionalContext: 'playground',
    });
    setSaveError(null);
    setSaveDialogOpen(true);
  };

  const handleSaveInstruction = async () => {
    if (!saveForm.name.trim()) {
      setSaveError('Name is required');
      return;
    }
    if (!tabState.systemPrompt?.trim()) {
      setSaveError('System prompt is empty. Please enter a system prompt first.');
      return;
    }

    setSaveLoading(true);
    setSaveError(null);

    try {
      await LlmService.createSystemInstruction({
        name: saveForm.name.trim(),
        description: saveForm.description.trim() || `Created from FAST Studio`,
        systemDomain: saveForm.systemDomain.trim() || 'custom',
        functionalContext: saveForm.functionalContext.trim() || 'playground',
        systemInstruction: tabState.systemPrompt.trim(),
      });
      setSaveDialogOpen(false);
      // Clear the instruction search to allow finding the new one
      setSelectedInstruction(null);
      setInstructionOptions([]);
    } catch (err) {
      setSaveError(err.message || 'Failed to save system instruction');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleOpenCopyDialog = () => {
    // Pre-fill with a suggested copy name
    const baseName = selectedInstruction?.name || 'system_instruction';
    setCopyName(`${baseName}_copy`);
    setCopyError(null);
    setCopyDialogOpen(true);
  };

  const handleCopyInstruction = async () => {
    if (!copyName.trim()) {
      setCopyError('Name is required');
      return;
    }
    if (!tabState.systemPrompt?.trim()) {
      setCopyError('System prompt is empty.');
      return;
    }

    setCopyLoading(true);
    setCopyError(null);

    try {
      await LlmService.createSystemInstruction({
        name: copyName.trim(),
        description: selectedInstruction ? `Copy of ${selectedInstruction.name}` : 'Copied from FAST Studio',
        systemDomain: 'custom',
        functionalContext: 'playground',
        systemInstruction: tabState.systemPrompt.trim(),
      });
      setCopyDialogOpen(false);
      // Clear the instruction search to allow finding the new one
      setSelectedInstruction(null);
      setInstructionOptions([]);
    } catch (err) {
      setCopyError(err.message || 'Failed to copy system instruction');
    } finally {
      setCopyLoading(false);
    }
  };

  // Check variables in system prompt and replace with resolved values
  const handleCheckVariables = async () => {
    if (!tabState.systemPrompt?.trim()) {
      return;
    }

    setIsCheckingVariables(true);
    setVariableCheckResult(null);

    // Store the raw prompt for revert functionality
    setRawSystemPrompt(tabState.systemPrompt);

    try {
      const result = await PromptService.checkPromptVariables(tabState.systemPrompt);
      setVariableCheckResult(result);

      // Update the system prompt with resolved values
      // Variables that couldn't resolve stay as {{varname}}
      if (result.processedPrompt) {
        setTabState(prev => ({ ...prev, systemPrompt: result.processedPrompt }));
        setProcessedSystemPrompt(result.processedPrompt);
      }
    } catch (err) {
      console.error('Failed to check variables:', err);
      setVariableCheckResult({
        success: false,
        error: err.message,
        variables: [],
        variableCount: 0,
        resolvedCount: 0,
      });
    } finally {
      setIsCheckingVariables(false);
    }
  };

  // Revert to original raw prompt
  const handleRevertPrompt = () => {
    if (rawSystemPrompt) {
      setTabState(prev => ({ ...prev, systemPrompt: rawSystemPrompt }));
      setRawSystemPrompt(null);
      setVariableCheckResult(null);
      setProcessedSystemPrompt(null);
    }
  };

  const handleSimulate = async () => {
    if (!tabState.systemPrompt?.trim()) {
      setTabState(prev => ({ ...prev, error: 'System prompt is required for simulation' }));
      return;
    }
    if (!tabState.userPrompt?.trim()) {
      setTabState(prev => ({ ...prev, error: 'User prompt is required for simulation' }));
      return;
    }
    if (!tabState.selectedModelId) {
      setTabState(prev => ({ ...prev, error: 'Please select a model in Settings' }));
      return;
    }

    setSimulateLoading(true);
    setTabState(prev => ({ ...prev, isLoading: true, error: null, response: null }));

    try {
      const result = await LlmService.simulateChat({
        systemPrompt: tabState.systemPrompt.trim(),
        userPrompt: tabState.userPrompt.trim(),
        modelId: tabState.selectedModelId,
        userId: tabState.simulateUserId?.trim() || '1',
        connectedSystemId: tabState.simulateConnectedSystemId?.trim() || null,
      });

      const rawResponse = result.rawLlmResponse || result.response || '';

      setTabState(prev => ({
        ...prev,
        response: {
          response: result.response,
          rawLlmResponse: rawResponse,
          model: result.model || 'Simulation',
          provider: 'FAST Orchestration',
          tokenUsage: null,
          processingTimeMs: result.processingTimeMs,
          finishReason: 'simulation',
          functionDetected: result.functionDetected,
          functionName: result.functionName,
          executionLog: result.executionLog,
          simulationDetails: {
            userId: result.userId,
            connectedSystemId: result.connectedSystemId,
            sessionId: result.sessionId,
          }
        },
        isLoading: false,
        error: null,
      }));

      // Capture processed system prompt from execution trace for viewing
      if (result.executionTrace?.processedSystemPrompt) {
        setProcessedSystemPrompt(result.executionTrace.processedSystemPrompt);
      }

      // Record test run for prompt management
      try {
        const testRunData = {
          promptId: currentPromptId || null,
          toolId: null, // Could be set if testing a tool directly
          testName: null,
          userPrompt: tabState.userPrompt.trim(),
          modelId: tabState.selectedModelId,
          modelName: result.model || 'Unknown',
          temperature: tabState.temperature,
          maxTokens: tabState.maxTokens,
          rawSystemPrompt: tabState.systemPrompt.trim(),
          processedSystemPrompt: result.executionTrace?.processedSystemPrompt || null,
          variableSubstitutions: result.executionTrace?.keywordSubstitutions || null,
          llmRequestPayload: result.executionTrace?.llmRequestPayload || null,
          llmResponsePayload: result.executionTrace?.llmResponsePayload || null,
          responseText: result.response || rawResponse,
          routingDecision: result.executionTrace?.routingDecision ? {
            route: result.executionTrace.routingDecision,
            routedTo: result.executionTrace.routedTo,
            reason: result.executionTrace.routingReason,
          } : null,
          routedTo: result.executionTrace?.routedTo || null,
          tokenUsageInput: result.executionTrace?.llmResponsePayload?.usage?.prompt_tokens || null,
          tokenUsageOutput: result.executionTrace?.llmResponsePayload?.usage?.completion_tokens || null,
          tokenUsageTotal: result.executionTrace?.llmResponsePayload?.usage?.total_tokens || null,
          latencyMs: result.processingTimeMs || null,
        };
        const testRun = await PromptService.recordPromptTestRun(testRunData);
        setCurrentTestRunId(testRun.id);
      } catch (testRunErr) {
        console.warn('Failed to record test run:', testRunErr);
        // Don't fail the whole simulation if test run recording fails
      }

      // Detect tools from the raw LLM response
      const detectedTools = await detectToolsFromResponse(rawResponse);

      if (detectedTools.length > 0) {
        setDebugState(prev => ({
          ...prev,
          mode: 'TOOLS_DETECTED',
          currentStep: 1,
          totalSteps: detectedTools.length + 2, // Tools + final LLM call
          sessionId: result.sessionId,
          detectedTools: detectedTools.map((t, idx) => ({
            ...t,
            status: idx === 0 ? 'current' : 'pending'
          })),
          accumulatedResponses: [],
          originalLlmResponse: rawResponse,
          error: null,
        }));
        setDebugPanelOpen(true);
      } else {
        setDebugState(prev => ({
          ...prev,
          mode: 'SIMULATED',
          currentStep: 1,
          totalSteps: 1,
          sessionId: result.sessionId,
          detectedTools: [],
          originalLlmResponse: rawResponse,
          error: null,
        }));
      }

    } catch (err) {
      setTabState(prev => ({ ...prev, error: err.message || 'Simulation failed', isLoading: false }));
      setDebugState(prev => ({ ...prev, mode: 'IDLE', error: err.message }));
    } finally {
      setSimulateLoading(false);
    }
  };

  // Handle Step Next - execute next tool or final LLM call
  const handleStepNext = async () => {
    const { mode, detectedTools, accumulatedResponses } = debugState;

    if (mode === 'IDLE' || mode === 'SIMULATED') {
      return;
    }

    setStepNextLoading(true);

    try {
      if (mode === 'TOOLS_DETECTED' || mode === 'EXECUTING') {
        // Find current tool to execute
        const currentToolIndex = detectedTools.findIndex(t => t.status === 'current');
        if (currentToolIndex === -1) {
          setDebugState(prev => ({ ...prev, mode: 'ALL_DONE', error: null }));
          setStepNextLoading(false);
          return;
        }

        const currentTool = detectedTools[currentToolIndex];

        // Check if tool has API endpoint
        if (!currentTool.apiEndpoint) {
          setDebugState(prev => ({
            ...prev,
            error: `No API endpoint defined for tool: ${currentTool.toolKey}`,
          }));
          setStepNextLoading(false);
          return;
        }

        // Use edited parameters if available, otherwise use defaults
        const params = editedParams[currentTool.toolKey] || currentTool.parameters || {};

        // Execute the tool
        const result = await LlmService.executeToolFunction(
          currentTool.toolId,
          currentTool.functionName || 'execute',
          params,
          tabState.simulateConnectedSystemId || null
        );

        // Update tool status and response
        const updatedTools = [...detectedTools];
        updatedTools[currentToolIndex] = {
          ...currentTool,
          status: 'done',
          response: result,
        };

        // Mark next tool as current if exists
        if (currentToolIndex + 1 < updatedTools.length) {
          updatedTools[currentToolIndex + 1].status = 'current';
        }

        const newAccumulated = [...accumulatedResponses, {
          toolKey: currentTool.toolKey,
          functionName: currentTool.functionName,
          response: result,
        }];

        const allToolsDone = updatedTools.every(t => t.status === 'done');

        setDebugState(prev => ({
          ...prev,
          mode: allToolsDone ? 'ALL_DONE' : 'EXECUTING',
          currentStep: prev.currentStep + 1,
          detectedTools: updatedTools,
          accumulatedResponses: newAccumulated,
          error: null,
        }));

        // Clear param editing for this tool
        setToolParamEditing(null);
        setEditedParams(prev => {
          const newParams = { ...prev };
          delete newParams[currentTool.toolKey];
          return newParams;
        });

      } else if (mode === 'ALL_DONE') {
        // Final step - send accumulated responses back to LLM
        const toolResponsesSummary = accumulatedResponses.map(r =>
          `Tool: ${r.toolKey}\nFunction: ${r.functionName}\nResponse: ${JSON.stringify(r.response?.data || r.response, null, 2)}`
        ).join('\n\n---\n\n');

        const finalPrompt = `Based on the following tool execution results, provide the final response to the user:\n\n${toolResponsesSummary}\n\nOriginal user request: ${tabState.userPrompt}`;

        const result = await LlmService.sendToLlmPlayground({
          systemPrompt: tabState.systemPrompt?.trim() || '',
          userPrompt: finalPrompt,
          modelId: tabState.selectedModelId,
          temperature: tabState.temperature,
          maxTokens: tabState.maxTokens,
        });

        setTabState(prev => ({
          ...prev,
          response: {
            ...prev.response,
            response: result.response,
            finalResponse: true,
          },
        }));

        setDebugState(prev => ({
          ...prev,
          mode: 'FINAL',
          currentStep: prev.currentStep + 1,
          error: null,
        }));
      }
    } catch (err) {
      console.error('Step Next error:', err);
      setDebugState(prev => ({
        ...prev,
        error: err.message || 'Step execution failed',
      }));
    } finally {
      setStepNextLoading(false);
    }
  };

  // Handle View Prompt - load tool's system prompt
  const handleViewPrompt = async (tool) => {
    if (tool.prompt) {
      setTabState(prev => ({ ...prev, systemPrompt: tool.prompt }));
    } else {
      try {
        const toolData = await LlmService.getTool(tool.toolId);
        if (toolData.prompt) {
          setTabState(prev => ({ ...prev, systemPrompt: toolData.prompt }));
        } else {
          setDebugState(prev => ({ ...prev, error: `No prompt found for tool: ${tool.toolKey}` }));
        }
      } catch (err) {
        setDebugState(prev => ({ ...prev, error: `Failed to load prompt: ${err.message}` }));
      }
    }
  };

  // Handle Reset - clear debug state
  const handleReset = async () => {
    // TODO: Optionally delete session from backend
    // if (debugState.sessionId) {
    //   try { await LlmService.deleteSession(debugState.sessionId); } catch (e) {}
    // }

    setDebugState({
      mode: 'IDLE',
      currentStep: 0,
      totalSteps: 0,
      sessionId: null,
      detectedTools: [],
      accumulatedResponses: [],
      wizardContext: {},
      originalLlmResponse: '',
      error: null,
    });
    setDebugPanelOpen(false);
    setToolParamEditing(null);
    setEditedParams({});
  };

  // Handle parameter editing
  const handleStartParamEdit = (toolKey, currentParams) => {
    setToolParamEditing(toolKey);
    setEditedParams(prev => ({
      ...prev,
      [toolKey]: { ...currentParams },
    }));
  };

  const handleParamChange = (toolKey, paramName, value) => {
    setEditedParams(prev => ({
      ...prev,
      [toolKey]: {
        ...(prev[toolKey] || {}),
        [paramName]: value,
      },
    }));
  };

  const handleCancelParamEdit = () => {
    setToolParamEditing(null);
  };

  // Calculate panel widths based on debug panel state
  const leftPanelWidth = debugPanelOpen ? '35%' : '50%';
  const middlePanelWidth = debugPanelOpen ? '30%' : '0%';
  const rightPanelWidth = debugPanelOpen ? '35%' : '50%';

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left Panel - Input */}
      <Box sx={{ width: leftPanelWidth, borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.3s ease' }}>
        {/* System Instruction Search */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>Load System Instruction</Typography>
          <Autocomplete
            size="small"
            options={instructionOptions}
            getOptionLabel={(option) => option.name || ''}
            value={selectedInstruction}
            onChange={handleInstructionSelect}
            onInputChange={handleInstructionSearch}
            onOpen={handleInstructionOpen}
            loading={instructionLoading}
            filterOptions={(x) => x} // Disable built-in filtering, use server-side
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>{option.description}</Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type to search system instructions..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    bgcolor: colors.surfaceElevated,
                    '& fieldset': { borderColor: colors.border },
                    '&.Mui-focused fieldset': { borderColor: colors.accent },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                }}
              />
            )}
            sx={{
              '& .MuiAutocomplete-paper': { bgcolor: colors.surface },
            }}
          />
        </Box>

        {/* System Prompt */}
        <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderBottom: `1px solid ${colors.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>System Prompt</Typography>
              <Tooltip title="Open Prompt Library">
                <IconButton
                  size="small"
                  onClick={() => setPromptLibraryOpen(true)}
                  sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.accent } }}
                >
                  <LibraryBooksIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {currentPromptId && (
                <Chip
                  size="small"
                  label="From Library"
                  onDelete={() => setCurrentPromptId(null)}
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: colors.accentSubtle,
                    color: colors.accent,
                    '& .MuiChip-deleteIcon': { color: colors.accent, fontSize: 14 },
                  }}
                />
              )}
              <ToggleButtonGroup
                value={promptViewMode}
                exclusive
                onChange={(e, newMode) => newMode && setPromptViewMode(newMode)}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    py: 0.25,
                    px: 1,
                    fontSize: '0.7rem',
                    borderRadius: 0,
                    borderColor: colors.border,
                    color: colors.textMuted,
                    '&.Mui-selected': {
                      bgcolor: colors.accentSubtle,
                      color: colors.accent,
                      borderColor: colors.accent,
                    },
                    '&:hover': {
                      bgcolor: colors.accentSubtle,
                    },
                  },
                }}
              >
                <ToggleButton value="raw">Raw Template</ToggleButton>
                <ToggleButton value="processed" disabled={!processedSystemPrompt}>
                  Processed
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Check if {{variables}} resolve correctly">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCheckVariables}
                  disabled={!tabState.systemPrompt?.trim() || isCheckingVariables}
                  startIcon={isCheckingVariables ? <CircularProgress size={14} /> : <CheckIcon />}
                  sx={{
                    ml: 1,
                    borderRadius: 0,
                    borderColor: variableCheckResult
                      ? (variableCheckResult.resolvedCount === variableCheckResult.variableCount ? colors.success : colors.error)
                      : colors.border,
                    color: variableCheckResult
                      ? (variableCheckResult.resolvedCount === variableCheckResult.variableCount ? colors.success : colors.error)
                      : colors.textSecondary,
                    fontSize: '0.75rem',
                    py: 0.25,
                    px: 1,
                    '&:hover': {
                      borderColor: colors.accent,
                      bgcolor: colors.accentSubtle,
                    },
                  }}
                >
                  Check
                </Button>
              </Tooltip>
              {variableCheckResult && (
                <Chip
                  size="small"
                  label={`${variableCheckResult.resolvedCount}/${variableCheckResult.variableCount}`}
                  sx={{
                    ml: 0.5,
                    fontSize: '0.65rem',
                    height: 20,
                    bgcolor: variableCheckResult.resolvedCount === variableCheckResult.variableCount
                      ? colors.successSubtle
                      : colors.errorSubtle,
                    color: variableCheckResult.resolvedCount === variableCheckResult.variableCount
                      ? colors.success
                      : colors.error,
                  }}
                />
              )}
              {rawSystemPrompt && (
                <Tooltip title="Revert to original prompt">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRevertPrompt}
                    startIcon={<UndoIcon />}
                    sx={{
                      ml: 1,
                      borderRadius: 0,
                      borderColor: colors.warning,
                      color: colors.warning,
                      fontSize: '0.75rem',
                      py: 0.25,
                      px: 1,
                      '&:hover': {
                        borderColor: colors.warningDark,
                        bgcolor: colors.warningSubtle,
                      },
                    }}
                  >
                    Revert
                  </Button>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {promptViewMode === 'processed' && processedSystemPrompt && (
                <Chip
                  size="small"
                  label="Read-only"
                  sx={{
                    fontSize: '0.65rem',
                    height: 18,
                    bgcolor: colors.warningSubtle,
                    color: colors.warning,
                  }}
                />
              )}
              {(promptViewMode === 'raw' ? tabState.systemPrompt?.trim() : processedSystemPrompt) && (
                <Tooltip title={`Copy ${promptViewMode === 'processed' ? 'processed' : 'raw'} prompt`}>
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(
                      promptViewMode === 'processed' ? processedSystemPrompt : tabState.systemPrompt
                    )}
                    sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.textPrimary } }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          {promptViewMode === 'raw' ? (
            rawSystemPrompt ? (
              // Show styled view with bold variables after Check is clicked
              <Box
                sx={{
                  flex: 1,
                  p: 1.5,
                  borderRadius: 0,
                  bgcolor: colors.surfaceElevated,
                  border: `1px solid ${colors.border}`,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  color: colors.textPrimary,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {renderTextWithBoldVariables(tabState.systemPrompt, variableCheckResult)}
              </Box>
            ) : (
              // Show editable TextField when no check has been performed
              <TextField
                multiline fullWidth placeholder="Enter system instructions..."
                value={tabState.systemPrompt || ''}
                onChange={(e) => {
                  setTabState(prev => ({ ...prev, systemPrompt: e.target.value }));
                  // Clear processed prompt and variable check when raw is edited
                  setProcessedSystemPrompt(null);
                  setVariableCheckResult(null);
                }}
                onKeyDown={handleKeyDown}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0, bgcolor: colors.surfaceElevated, height: '100%', alignItems: 'flex-start',
                    '& fieldset': { borderColor: colors.border },
                    '&.Mui-focused fieldset': { borderColor: colors.accent },
                  },
                  '& .MuiInputBase-input': { color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.875rem', height: '100% !important', overflow: 'auto !important' },
                }}
              />
            )
          ) : (
            <Box
              sx={{
                flex: 1,
                p: 1.5,
                borderRadius: 0,
                bgcolor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                color: colors.textPrimary,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {processedSystemPrompt ? (
                renderTextWithBoldVariables(processedSystemPrompt, null)
              ) : (
                <Typography sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
                  Run Simulate to see the processed prompt sent to LLM
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* User Prompt */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>User Prompt *</Typography>
            {tabState.userPrompt?.trim() && (
              <Tooltip title="Copy user prompt">
                <IconButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(tabState.userPrompt)}
                  sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.textPrimary } }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <TextField
            multiline rows={3} fullWidth placeholder="Enter your prompt here... (Ctrl+Enter to send)"
            value={tabState.userPrompt || ''}
            onChange={(e) => setTabState(prev => ({ ...prev, userPrompt: e.target.value }))}
            onKeyDown={handleKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0, bgcolor: colors.surfaceElevated,
                '& fieldset': { borderColor: colors.border },
                '&.Mui-focused fieldset': { borderColor: colors.accent },
              },
              '& .MuiInputBase-input': { color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ p: 2, borderTop: `1px solid ${colors.border}`, display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Tooltip title="Save system prompt as new instruction">
            <Button variant="outlined" size="small" startIcon={<SaveIcon />} onClick={handleOpenSaveDialog}
              disabled={!tabState.systemPrompt?.trim()}
              sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0, '&:hover': { borderColor: colors.success, color: colors.success, bgcolor: colors.successSubtle }, '&.Mui-disabled': { borderColor: colors.border, color: colors.textMuted } }}>
              Save
            </Button>
          </Tooltip>
          <Tooltip title="Make a copy with new name">
            <Button variant="outlined" size="small" startIcon={<FileCopyIcon />} onClick={handleOpenCopyDialog}
              disabled={!tabState.systemPrompt?.trim()}
              sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0, '&:hover': { borderColor: colors.info, color: colors.info, bgcolor: colors.infoSubtle }, '&.Mui-disabled': { borderColor: colors.border, color: colors.textMuted } }}>
              Copy
            </Button>
          </Tooltip>
          <Button variant="outlined" size="small" startIcon={<ClearIcon />} onClick={handleClear}
            sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0, '&:hover': { borderColor: colors.textSecondary, bgcolor: 'rgba(255,255,255,0.05)' } }}>
            Clear
          </Button>
          <Button variant="contained" size="small" startIcon={tabState.isLoading ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            onClick={handleSend} disabled={tabState.isLoading || !tabState.userPrompt?.trim() || !tabState.selectedModelId}
            sx={{ bgcolor: colors.accent, borderRadius: 0, '&:hover': { bgcolor: colors.accentDark }, '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted } }}>
            {tabState.isLoading ? 'Sending...' : 'Send'}
          </Button>
          <Tooltip title="Simulate with FAST orchestration">
            <Button variant="contained" size="small" startIcon={simulateLoading ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleSimulate}
              disabled={simulateLoading || tabState.isLoading || !tabState.systemPrompt?.trim() || !tabState.userPrompt?.trim()}
              sx={{ bgcolor: colors.warning, borderRadius: 0, '&:hover': { bgcolor: colors.warningDark }, '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted } }}>
              Simulate
            </Button>
          </Tooltip>
          <Tooltip title="Execute next tool or finalize">
            <Button variant="contained" size="small" startIcon={stepNextLoading ? <CircularProgress size={14} color="inherit" /> : <SkipNextIcon />}
              onClick={handleStepNext}
              disabled={stepNextLoading || debugState.mode === 'IDLE' || debugState.mode === 'SIMULATED' || debugState.mode === 'FINAL'}
              sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark }, '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted } }}>
              Step Next
            </Button>
          </Tooltip>
          <Tooltip title="Reset debug session">
            <Button variant="outlined" size="small" startIcon={<RestartAltIcon />} onClick={handleReset}
              disabled={debugState.mode === 'IDLE'}
              sx={{ borderColor: colors.error, color: colors.error, borderRadius: 0, '&:hover': { bgcolor: colors.errorSubtle }, '&.Mui-disabled': { borderColor: colors.border, color: colors.textMuted } }}>
              Reset
            </Button>
          </Tooltip>
        </Box>

        {/* Save Dialog */}
        <Dialog
          open={saveDialogOpen}
          onClose={() => setSaveDialogOpen(false)}
          PaperProps={{
            sx: { bgcolor: colors.background, borderRadius: 0, minWidth: 450 }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 3 }}>
              Save System Instruction
            </Typography>

            {saveError && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
                <Typography variant="body2" sx={{ color: colors.error }}>{saveError}</Typography>
              </Box>
            )}

            <TextField
              label="Name *" fullWidth size="small"
              placeholder="e.g., my_custom_agent"
              value={saveForm.name}
              onChange={(e) => setSaveForm(prev => ({ ...prev, name: e.target.value }))}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />

            <TextField
              label="Description" fullWidth size="small" multiline rows={2}
              placeholder="Brief description of what this instruction does"
              value={saveForm.description}
              onChange={(e) => setSaveForm(prev => ({ ...prev, description: e.target.value }))}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label="Domain" fullWidth size="small"
                placeholder="e.g., warehouse, custom"
                value={saveForm.systemDomain}
                onChange={(e) => setSaveForm(prev => ({ ...prev, systemDomain: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
              <TextField
                label="Context" fullWidth size="small"
                placeholder="e.g., agent, playground"
                value={saveForm.functionalContext}
                onChange={(e) => setSaveForm(prev => ({ ...prev, functionalContext: e.target.value }))}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                }}
              />
            </Box>

            <Box sx={{ p: 2, bgcolor: colors.surfaceElevated, border: `1px solid ${colors.border}`, borderRadius: 0, mb: 3 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                System Prompt Preview (first 200 chars):
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {tabState.systemPrompt?.substring(0, 200)}{tabState.systemPrompt?.length > 200 ? '...' : ''}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setSaveDialogOpen(false)}
                sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveInstruction}
                disabled={saveLoading || !saveForm.name.trim()}
                startIcon={saveLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark } }}
              >
                {saveLoading ? 'Saving...' : 'Save Instruction'}
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* Copy Dialog */}
        <Dialog
          open={copyDialogOpen}
          onClose={() => setCopyDialogOpen(false)}
          PaperProps={{
            sx: { bgcolor: colors.background, borderRadius: 0, minWidth: 400 }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ color: colors.textPrimary, mb: 2 }}>
              Copy System Instruction
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
              Enter a new name for the copy:
            </Typography>

            {copyError && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
                <Typography variant="body2" sx={{ color: colors.error }}>{copyError}</Typography>
              </Box>
            )}

            <TextField
              label="New Name *" fullWidth size="small" autoFocus
              placeholder="e.g., my_instruction_copy"
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCopyInstruction()}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                '& .MuiInputBase-input': { color: colors.textPrimary },
                '& .MuiInputLabel-root': { color: colors.textSecondary },
              }}
            />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" onClick={() => setCopyDialogOpen(false)}
                sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleCopyInstruction}
                disabled={copyLoading || !copyName.trim()}
                startIcon={copyLoading ? <CircularProgress size={16} color="inherit" /> : <FileCopyIcon />}
                sx={{ bgcolor: colors.info, borderRadius: 0, '&:hover': { bgcolor: colors.infoDark } }}
              >
                {copyLoading ? 'Copying...' : 'Create Copy'}
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>

      {/* Middle Panel - Debug State (Collapsible) */}
      {debugPanelOpen && (
        <Box sx={{
          width: middlePanelWidth,
          borderRight: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: colors.surface,
          transition: 'width 0.3s ease',
        }}>
          {/* Debug Panel Header */}
          <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ color: colors.success, fontWeight: 600 }}>
              Debug State
            </Typography>
            <IconButton size="small" onClick={() => setDebugPanelOpen(false)} sx={{ color: colors.textMuted, p: 0.25 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Debug State Info */}
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>Mode:</Typography>
              <Chip
                label={debugState.mode}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.65rem',
                  bgcolor: debugState.mode === 'FINAL' ? colors.successSubtle :
                           debugState.mode === 'EXECUTING' ? colors.warningSubtle :
                           debugState.mode === 'ALL_DONE' ? colors.infoSubtle :
                           colors.accentSubtle,
                  color: debugState.mode === 'FINAL' ? colors.success :
                         debugState.mode === 'EXECUTING' ? colors.warning :
                         debugState.mode === 'ALL_DONE' ? colors.info :
                         colors.accent,
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>Step:</Typography>
              <Typography variant="caption" sx={{ color: colors.textPrimary }}>{debugState.currentStep} / {debugState.totalSteps}</Typography>
            </Box>
            {debugState.sessionId && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: colors.textMuted }}>Session:</Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem' }}>{debugState.sessionId.slice(0, 12)}...</Typography>
              </Box>
            )}
          </Box>

          {/* Error Display */}
          {debugState.error && (
            <Box sx={{ p: 1.5, bgcolor: 'rgba(239,68,68,0.1)', borderBottom: `1px solid ${colors.border}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ErrorIcon sx={{ fontSize: 14, color: colors.error }} />
                <Typography variant="caption" sx={{ color: colors.error }}>{debugState.error}</Typography>
              </Box>
            </Box>
          )}

          {/* Detected Tools */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                DETECTED TOOLS ({debugState.detectedTools.length})
              </Typography>
              {debugState.detectedTools.length === 0 ? (
                <Typography variant="caption" sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
                  No tools detected yet
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {debugState.detectedTools.map((tool, idx) => (
                    <Box key={tool.toolKey} sx={{
                      p: 1,
                      bgcolor: tool.status === 'current' ? colors.warningSubtle :
                               tool.status === 'done' ? colors.successSubtle :
                               colors.surfaceElevated,
                      border: `1px solid ${tool.status === 'current' ? colors.warning :
                                           tool.status === 'done' ? colors.success :
                                           colors.border}`,
                      borderRadius: 0,
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {tool.status === 'done' ? (
                            <CheckCircleIcon sx={{ fontSize: 14, color: colors.success }} />
                          ) : tool.status === 'current' ? (
                            <PlayCircleOutlineIcon sx={{ fontSize: 14, color: colors.warning }} />
                          ) : (
                            <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                          )}
                          <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                            {idx + 1}. {tool.name || tool.toolKey}
                          </Typography>
                        </Box>
                        <Tooltip title="View prompt">
                          <IconButton size="small" onClick={() => handleViewPrompt(tool)} sx={{ p: 0.25, color: colors.textMuted }}>
                            <VisibilityIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', display: 'block' }}>
                        {tool.apiEndpoint || 'No endpoint'}
                      </Typography>

                      {/* Parameter Editing for current tool */}
                      {tool.status === 'current' && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${colors.border}` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: colors.textSecondary, fontSize: '0.65rem' }}>
                              Parameters:
                            </Typography>
                            {toolParamEditing !== tool.toolKey ? (
                              <IconButton size="small" onClick={() => handleStartParamEdit(tool.toolKey, tool.parameters || {})} sx={{ p: 0.25, color: colors.textMuted }}>
                                <EditIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            ) : (
                              <Button size="small" onClick={handleCancelParamEdit} sx={{ fontSize: '0.6rem', p: 0, minWidth: 'auto', color: colors.textMuted }}>
                                Done
                              </Button>
                            )}
                          </Box>
                          {toolParamEditing === tool.toolKey ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {Object.entries(editedParams[tool.toolKey] || {}).map(([key, value]) => (
                                <TextField
                                  key={key}
                                  size="small"
                                  label={key}
                                  value={value}
                                  onChange={(e) => handleParamChange(tool.toolKey, key, e.target.value)}
                                  sx={{
                                    '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                                    '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.7rem', p: 0.5 },
                                    '& .MuiInputLabel-root': { fontSize: '0.65rem', color: colors.textMuted },
                                  }}
                                />
                              ))}
                              <TextField
                                size="small"
                                placeholder="Add param: key=value"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && e.target.value.includes('=')) {
                                    const [key, ...rest] = e.target.value.split('=');
                                    handleParamChange(tool.toolKey, key.trim(), rest.join('=').trim());
                                    e.target.value = '';
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                                  '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.7rem', p: 0.5 },
                                }}
                              />
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', fontFamily: 'monospace' }}>
                              {Object.keys(editedParams[tool.toolKey] || tool.parameters || {}).length > 0
                                ? JSON.stringify(editedParams[tool.toolKey] || tool.parameters, null, 1)
                                : '{}'}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Tool Response */}
                      {tool.status === 'done' && tool.response && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${colors.border}` }}>
                          <Typography variant="caption" sx={{ color: colors.success, fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                            Response:
                          </Typography>
                          <Box sx={{
                            maxHeight: 80,
                            overflow: 'auto',
                            bgcolor: colors.background,
                            p: 0.5,
                            borderRadius: 0,
                          }}>
                            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(tool.response?.data || tool.response, null, 2).slice(0, 500)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Accumulated Responses */}
            {debugState.accumulatedResponses.length > 0 && (
              <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}` }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                  ACCUMULATED ({debugState.accumulatedResponses.length})
                </Typography>
                <Box sx={{ maxHeight: 100, overflow: 'auto', bgcolor: colors.surfaceElevated, p: 1, borderRadius: 0 }}>
                  {debugState.accumulatedResponses.map((r, idx) => (
                    <Typography key={idx} variant="caption" sx={{ color: colors.textMuted, fontSize: '0.6rem', display: 'block' }}>
                      {idx + 1}. {r.toolKey}: {r.response?.success ? '✓' : '✗'}
                    </Typography>
                  ))}
                </Box>
              </Box>
            )}

            {/* Wizard Context */}
            {Object.keys(debugState.wizardContext).length > 0 && (
              <Box sx={{ p: 1.5 }}>
                <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                  WIZARD CONTEXT
                </Typography>
                <Box sx={{ bgcolor: colors.surfaceElevated, p: 1, borderRadius: 0 }}>
                  {Object.entries(debugState.wizardContext).map(([key, value]) => (
                    <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>{key}:</Typography>
                      <Typography variant="caption" sx={{ color: colors.textPrimary, fontSize: '0.65rem' }}>{String(value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>

          {/* Expand Button when collapsed - shown on right edge */}
        </Box>
      )}

      {/* Collapse/Expand Toggle (shown when panel is closed) */}
      {!debugPanelOpen && debugState.mode !== 'IDLE' && (
        <Box sx={{
          width: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRight: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
          cursor: 'pointer',
          '&:hover': { bgcolor: colors.surfaceElevated },
        }} onClick={() => setDebugPanelOpen(true)}>
          <ChevronRightIcon sx={{ color: colors.success, fontSize: 18 }} />
        </Box>
      )}

      {/* Right Panel - Response */}
      <Box sx={{ width: rightPanelWidth, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'width 0.3s ease' }}>
        <Box sx={{ px: 1, py: 0.75, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>Response</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {tabState.response && tabState.response.tokenUsage !== undefined && (
              <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem', mr: 0.5 }}>
                {tabState.response.tokenUsage} tokens | {tabState.response.processingTimeMs}ms
              </Typography>
            )}
            {tabState.response && (
              <Tooltip title="Copy response">
                <IconButton size="small" onClick={handleCopyResponse} sx={{ color: colors.textMuted, p: 0.25, '&:hover': { color: colors.textPrimary } }}>
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Settings (Model & Simulation)">
              <IconButton
                size="small"
                onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                sx={{ color: settingsOpen ? colors.accent : colors.textMuted, '&:hover': { color: colors.accent }, p: 0.25 }}
              >
                <SettingsIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Settings Popover */}
        <Popover
          open={settingsOpen}
          anchorEl={settingsAnchorEl}
          onClose={() => setSettingsAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0, p: 2, minWidth: 320 }
          }}
        >
          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, mb: 2, fontWeight: 600 }}>Model Settings</Typography>

          <FormControl size="small" fullWidth sx={{ mb: 2 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Model</InputLabel>
            <Select
              value={tabState.selectedModelId || ''}
              onChange={(e) => setTabState(prev => ({ ...prev, selectedModelId: e.target.value }))}
              label="Model"
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '& .MuiSelect-select': { color: colors.textPrimary },
              }}
            >
              {availableModels.map((m) => (
                <MenuItem key={m.modelId} value={m.modelId}>
                  {m.modelName ? m.modelName.split('/').pop() : m.modelId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: colors.textSecondary }}>
              Temperature: {tabState.temperature}
            </Typography>
            <Slider
              value={tabState.temperature}
              onChange={(e, v) => setTabState(prev => ({ ...prev, temperature: v }))}
              min={0} max={1} step={0.1} size="small"
              sx={{ color: colors.accent, '& .MuiSlider-thumb': { borderRadius: 0 } }}
            />
          </Box>

          <TextField
            label="Max Tokens" type="number" size="small" fullWidth
            value={tabState.maxTokens}
            onChange={(e) => setTabState(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 2048 }))}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
            }}
          />

          {/* Divider */}
          <Box sx={{ borderTop: `1px solid ${colors.border}`, my: 2 }} />

          <Typography variant="subtitle2" sx={{ color: colors.warning, mb: 2, fontWeight: 600 }}>Simulation Settings</Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 2 }}>
            Used when clicking "Simulate" to test with FAST orchestration
          </Typography>

          <TextField
            label="User ID" size="small" fullWidth
            placeholder="e.g., 1"
            value={tabState.simulateUserId || ''}
            onChange={(e) => setTabState(prev => ({ ...prev, simulateUserId: e.target.value }))}
            helperText="User ID to simulate as (defaults to 1)"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: '0.65rem' },
            }}
          />

          <TextField
            label="Warehouse ID (Optional)" size="small" fullWidth
            placeholder="e.g., WH001"
            value={tabState.simulateWarehouseId || ''}
            onChange={(e) => setTabState(prev => ({ ...prev, simulateWarehouseId: e.target.value }))}
            helperText="Optional warehouse context for simulation"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary },
              '& .MuiInputLabel-root': { color: colors.textSecondary },
              '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: '0.65rem' },
            }}
          />
        </Popover>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: colors.surfaceElevated }}>
          {tabState.isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: colors.accent, mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>Waiting for LLM response...</Typography>
            </Box>
          ) : tabState.error ? (
            <Box sx={{ p: 2, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
              <Typography sx={{ color: colors.error, fontWeight: 500, mb: 1 }}>Error</Typography>
              <Typography sx={{ color: colors.textSecondary }}>{tabState.error}</Typography>
            </Box>
          ) : tabState.response ? (
            <Box>
              {/* Main Response */}
              <Box sx={{
                '& p': { color: colors.textPrimary, lineHeight: 1.7, mb: 2 },
                '& h1, & h2, & h3': { color: colors.textPrimary, mt: 2, mb: 1 },
                '& code': { bgcolor: colors.surface, px: 0.5, py: 0.25, borderRadius: '2px', fontFamily: 'monospace', fontSize: '0.875em', color: colors.accent },
                '& pre': { bgcolor: colors.surface, p: 2, borderRadius: 0, overflow: 'auto', '& code': { bgcolor: 'transparent', p: 0, color: colors.textPrimary } },
                '& ul, & ol': { color: colors.textPrimary, pl: 3 },
                '& blockquote': { borderLeft: `3px solid ${colors.accent}`, pl: 2, ml: 0, color: colors.textSecondary },
              }}>
                <ReactMarkdown>{tabState.response.response}</ReactMarkdown>
              </Box>

              {/* Raw LLM Response (for simulations) */}
              {tabState.response.rawLlmResponse && tabState.response.finishReason === 'simulation' && (
                <Box sx={{ mt: 3, borderTop: `1px solid ${colors.border}`, pt: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: colors.warning, mb: 1, fontWeight: 600 }}>
                    Raw LLM Response
                  </Typography>
                  {tabState.response.functionDetected && (
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
                      Function detected: <code style={{ color: colors.accent }}>{tabState.response.functionName || 'unknown'}</code>
                    </Typography>
                  )}
                  <Box sx={{
                    p: 1.5,
                    bgcolor: colors.surface,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 0,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    color: colors.textSecondary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}>
                    {tabState.response.rawLlmResponse}
                  </Box>

                  {/* Execution Log */}
                  {tabState.response.executionLog && tabState.response.executionLog.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontWeight: 600 }}>
                        Execution Log:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {tabState.response.executionLog.map((log, idx) => (
                          <Typography key={idx} variant="caption" sx={{ color: colors.textMuted, display: 'block', fontSize: '0.7rem', pl: 1 }}>
                            {idx + 1}. {log}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Test Run Rating */}
                  <TestRunRating
                    testRunId={currentTestRunId}
                    currentRating={null}
                    latencyMs={tabState.response.processingTimeMs}
                    tokenUsage={{
                      input: tabState.response.executionTrace?.llmResponsePayload?.usage?.prompt_tokens,
                      output: tabState.response.executionTrace?.llmResponsePayload?.usage?.completion_tokens,
                    }}
                    modelName={tabState.response.model}
                  />
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <PsychologyIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography sx={{ color: colors.textMuted }}>Enter a prompt and click "Send to LLM"</Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 1 }}>Tip: Use Ctrl+Enter to send</Typography>
            </Box>
          )}
        </Box>

        {tabState.response && (
          <Box sx={{ p: 1, borderTop: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem', display: 'block' }}>
              {tabState.response.model} | temp {tabState.temperature} | max {tabState.maxTokens}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem' }}>
              {tabState.response.provider} | {tabState.response.finishReason}
              {tabState.response.simulationDetails && (
                <> | user: {tabState.response.simulationDetails.userId}
                {tabState.response.simulationDetails.connectedSystemId && <> | system: {tabState.response.simulationDetails.connectedSystemId}</>}
                </>
              )}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Prompt Library Panel */}
      <PromptLibraryPanel
        open={promptLibraryOpen}
        onClose={() => setPromptLibraryOpen(false)}
        onSelectPrompt={handleSelectPromptFromLibrary}
        tools={availableTools}
      />

      {/* Variable Hover Popover - shows troubleshooting info for unresolved variables */}
      <Popover
        open={Boolean(variablePopover.anchorEl)}
        anchorEl={variablePopover.anchorEl}
        onClose={handleVariableHoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        disableRestoreFocus
        sx={{
          '& .MuiPaper-root': {
            bgcolor: colors.surfaceElevated,
            border: `1px solid ${colors.border}`,
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          },
        }}
      >
        <Box sx={{ p: 1.5, minWidth: 200, maxWidth: 350 }}>
          {variablePopover.varInfo && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {variablePopover.varInfo.exists ? (
                  <>
                    <BuildIcon sx={{ fontSize: 18, color: colors.warning }} />
                    <Typography variant="subtitle2" sx={{ color: colors.warning, fontWeight: 600 }}>
                      Troubleshoot
                    </Typography>
                  </>
                ) : (
                  <>
                    <ErrorIcon sx={{ fontSize: 18, color: colors.error }} />
                    <Typography variant="subtitle2" sx={{ color: colors.error, fontWeight: 600 }}>
                      Not Found
                    </Typography>
                  </>
                )}
              </Box>
              <Typography variant="body2" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>
                {variablePopover.varInfo.message || (
                  variablePopover.varInfo.exists
                    ? 'Variable exists but could not be resolved. Check if context data is available.'
                    : 'Unknown variable name. Check spelling or use a valid source:key.property format.'
                )}
              </Typography>
              <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${colors.border}` }}>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace' }}>
                  {variablePopover.varInfo.variable || variablePopover.varInfo.fullMatch}
                </Typography>
              </Box>
              {/* Show Create Script button for variables that don't exist */}
              {!variablePopover.varInfo.exists && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<CodeIcon />}
                  onClick={() => handleCreateScriptForVariable(variablePopover.varInfo)}
                  sx={{
                    mt: 1.5,
                    bgcolor: colors.accent,
                    color: '#fff',
                    fontSize: '0.75rem',
                    py: 0.5,
                    '&:hover': {
                      bgcolor: colors.accentDark,
                    },
                  }}
                >
                  Create Script
                </Button>
              )}
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

// ============================================================================
// API Hound Content Component
// ============================================================================

// Common headers that users frequently need
const COMMON_HEADERS = [
  { label: 'Authorization (Bearer)', value: 'Authorization: Bearer <token>' },
  { label: 'Content-Type (JSON)', value: 'Content-Type: application/json' },
  { label: 'Content-Type (Form)', value: 'Content-Type: application/x-www-form-urlencoded' },
  { label: 'Accept (JSON)', value: 'Accept: application/json' },
  { label: 'Accept (All)', value: 'Accept: */*' },
  { label: 'Cache-Control', value: 'Cache-Control: no-cache' },
  { label: 'X-Request-ID', value: 'X-Request-ID: <uuid>' },
];

const DEFAULT_HEADERS = `Content-Type: application/json
Accept: application/json`;

// Protected headers that are auto-injected - users cannot override these
const PROTECTED_HEADERS = [
  'x-fast-api-key',
  'x-tenant-id',
  'x-user-id',
  'x-warehouse-id',
  'x-system-code',
  'x-username',
];

const ApiTabContent = ({ colors, tabState, setTabState }) => {
  const [jsonValidation, setJsonValidation] = useState({ valid: null, message: '' });
  const [headerSelectValue, setHeaderSelectValue] = useState('');
  const [availableApis, setAvailableApis] = useState([]);
  const [apisLoading, setApisLoading] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [headerWarning, setHeaderWarning] = useState('');

  // Fetch available APIs and user's warehouses on mount
  useEffect(() => {
    const fetchApis = async () => {
      setApisLoading(true);
      try {
        const apis = await FlexService.listApis({ activeOnly: true });
        setAvailableApis(apis || []);
      } catch (err) {
        console.error('Failed to fetch APIs:', err);
        setAvailableApis([]);
      } finally {
        setApisLoading(false);
      }
    };

    const fetchWarehouses = async () => {
      try {
        const systems = await LlmService.getUserConnectedSystems();
        setWarehouses(systems || []);
        // Default to first warehouse if available
        if (systems && systems.length > 0) {
          setSelectedWarehouse(systems[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch warehouses:', err);
        setWarehouses([]);
      }
    };

    fetchApis();
    fetchWarehouses();
  }, []);

  // Handle API selection from dropdown
  const handleApiSelect = async (event, value) => {
    if (!value) {
      // Cleared selection
      return;
    }
    if (typeof value === 'string') {
      // User typed a custom URL - no auto headers
      setTabState(prev => ({ ...prev, url: value }));
    } else if (value.fullUrl || value.endpointPath) {
      // Selected an API from the list - use fullUrl from backend
      const newState = {
        url: value.fullUrl || value.endpointPath,
        method: value.httpMethod || 'GET',
        body: '', // Clear body when switching APIs
        headers: '', // Default empty
        response: null, // Clear previous response
        error: null, // Clear previous error
        isConnectedSystem: false, // Track if this is a managed system
      };

      // If API has a systemCode (WMS or other connected system), mark it
      // Auth headers are auto-injected by the proxy - don't put them in the textbox
      if (value.systemCode) {
        newState.isConnectedSystem = true;
        newState.systemCode = value.systemCode;
        // Keep headers empty - user can add custom headers, proxy adds auth headers
      }

      // If API has sampleRequest and method requires body, populate the body field
      if (value.sampleRequest && ['POST', 'PUT', 'PATCH'].includes(value.httpMethod)) {
        try {
          // Format the JSON for readability
          const parsed = JSON.parse(value.sampleRequest);
          newState.body = JSON.stringify(parsed, null, 2);
        } catch (e) {
          // If not valid JSON, use as-is
          newState.body = value.sampleRequest;
        }
      }

      setTabState(prev => ({ ...prev, ...newState }));
    }
  };

  const handleSend = async () => {
    if (!tabState.url?.trim()) {
      setTabState(prev => ({ ...prev, error: 'URL is required' }));
      return;
    }

    setTabState(prev => ({ ...prev, isLoading: true, error: null, response: null }));

    try {
      // Parse headers from text into object
      const headers = {};
      if (tabState.headers) {
        tabState.headers.split('\n').forEach(line => {
          const [key, ...valueParts] = line.split(':');
          if (key && valueParts.length > 0) {
            headers[key.trim()] = valueParts.join(':').trim();
          }
        });
      }

      // Use URL as-is (backend now provides full URLs)
      let fullUrl = tabState.url.trim();

      // Use the proxy service to avoid CORS issues
      // Pass selected warehouse for testing different warehouse contexts
      const result = await LlmService.proxyApiRequest({
        url: fullUrl,
        method: tabState.method || 'GET',
        headers: headers,
        body: ['POST', 'PUT', 'PATCH'].includes(tabState.method) ? tabState.body : null,
        warehouseId: selectedWarehouse || undefined,
      });

      if (!result.success) {
        // Proxy returned an error
        setTabState(prev => ({
          ...prev,
          isLoading: false,
          error: result.error || 'Request failed',
        }));
        return;
      }

      // Format JSON response body if applicable
      let responseBody = result.body || '';
      try {
        const parsed = JSON.parse(responseBody);
        responseBody = JSON.stringify(parsed, null, 2);
      } catch (e) {
        // Not JSON, keep as-is
      }

      setTabState(prev => ({
        ...prev,
        isLoading: false,
        error: null, // Clear any previous error
        response: {
          status: result.status,
          statusText: result.statusText,
          body: responseBody,
          time: result.time,
          headers: result.headers || {},
        },
      }));
    } catch (err) {
      setTabState(prev => ({ ...prev, error: err.message || 'Request failed', isLoading: false, response: null }));
    }
  };

  const handleClear = () => {
    setTabState(prev => ({ ...prev, body: '', response: null, error: null }));
    setJsonValidation({ valid: null, message: '' });
  };

  const handleCopyResponse = () => {
    if (tabState.response?.body) {
      navigator.clipboard.writeText(tabState.response.body);
    }
  };

  // Add common headers to the headers text box
  const handleAddCommonHeaders = () => {
    const currentHeaders = tabState.headers?.trim() || '';
    const newHeaders = currentHeaders
      ? `${currentHeaders}\n${DEFAULT_HEADERS}`
      : DEFAULT_HEADERS;
    setTabState(prev => ({ ...prev, headers: newHeaders }));
  };

  // Add selected header from dropdown
  const handleAddHeader = (headerValue) => {
    if (!headerValue) return;
    const currentHeaders = tabState.headers?.trim() || '';
    const newHeaders = currentHeaders
      ? `${currentHeaders}\n${headerValue}`
      : headerValue;
    setTabState(prev => ({ ...prev, headers: newHeaders }));
    setHeaderSelectValue('');
  };

  // Check for protected headers in custom headers input
  const validateHeaders = (headersText) => {
    if (!headersText || !tabState.isConnectedSystem) {
      setHeaderWarning('');
      return;
    }

    const lines = headersText.split('\n');
    const foundProtected = [];

    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex > 0) {
        const headerName = line.substring(0, colonIndex).trim().toLowerCase();
        if (PROTECTED_HEADERS.includes(headerName)) {
          foundProtected.push(line.substring(0, colonIndex).trim());
        }
      }
    }

    if (foundProtected.length > 0) {
      setHeaderWarning(`Protected headers will be ignored: ${foundProtected.join(', ')}`);
    } else {
      setHeaderWarning('');
    }
  };

  // Handle headers change with validation
  const handleHeadersChange = (e) => {
    const newHeaders = e.target.value;
    setTabState(prev => ({ ...prev, headers: newHeaders }));
    validateHeaders(newHeaders);
  };

  // Validate JSON in body
  const handleValidateJson = () => {
    const body = tabState.body?.trim();
    if (!body) {
      setJsonValidation({ valid: false, message: 'Body is empty' });
      return;
    }
    try {
      JSON.parse(body);
      setJsonValidation({ valid: true, message: 'Valid JSON' });
    } catch (e) {
      setJsonValidation({ valid: false, message: `Invalid JSON: ${e.message}` });
    }
  };

  // Format JSON in body
  const handleFormatJson = () => {
    const body = tabState.body?.trim();
    if (!body) {
      setJsonValidation({ valid: false, message: 'Body is empty' });
      return;
    }
    try {
      const parsed = JSON.parse(body);
      const formatted = JSON.stringify(parsed, null, 2);
      setTabState(prev => ({ ...prev, body: formatted }));
      setJsonValidation({ valid: true, message: 'JSON formatted' });
    } catch (e) {
      setJsonValidation({ valid: false, message: `Cannot format - Invalid JSON: ${e.message}` });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
      {/* Top Bar - URL and Method (full width) */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ width: 120 }}>
            <Select
              value={tabState.method || 'GET'}
              onChange={(e) => setTabState(prev => ({ ...prev, method: e.target.value }))}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '& .MuiSelect-select': {
                  color: tabState.method === 'GET' ? colors.httpGet :
                         tabState.method === 'POST' ? colors.httpPost :
                         tabState.method === 'PUT' ? colors.httpPut :
                         tabState.method === 'DELETE' ? colors.httpDelete : colors.httpPatch,
                  fontWeight: 600,
                },
              }}
            >
              <MenuItem value="GET" sx={{ color: colors.httpGet, fontWeight: 600 }}>GET</MenuItem>
              <MenuItem value="POST" sx={{ color: colors.httpPost, fontWeight: 600 }}>POST</MenuItem>
              <MenuItem value="PUT" sx={{ color: colors.httpPut, fontWeight: 600 }}>PUT</MenuItem>
              <MenuItem value="PATCH" sx={{ color: colors.httpPatch, fontWeight: 600 }}>PATCH</MenuItem>
              <MenuItem value="DELETE" sx={{ color: colors.httpDelete, fontWeight: 600 }}>DELETE</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            freeSolo
            size="small"
            fullWidth
            loading={apisLoading}
            options={availableApis}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const url = option.fullUrl || option.endpointPath || '';
              return option.apiName ? `${option.apiName} - ${url}` : url;
            }}
            value={tabState.url || ''}
            onChange={handleApiSelect}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') {
                setTabState(prev => ({ ...prev, url: value }));
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option.endpointPath}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={option.httpMethod || 'GET'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        bgcolor: option.httpMethod === 'GET' ? colors.httpGetBg :
                                 option.httpMethod === 'POST' ? colors.httpPostBg :
                                 option.httpMethod === 'PUT' ? colors.httpPutBg :
                                 option.httpMethod === 'DELETE' ? colors.httpDeleteBg : colors.httpPatchBg,
                        color: option.httpMethod === 'GET' ? colors.httpGet :
                               option.httpMethod === 'POST' ? colors.httpPost :
                               option.httpMethod === 'PUT' ? colors.httpPut :
                               option.httpMethod === 'DELETE' ? colors.httpDelete : colors.httpPatch,
                      }}
                    />
                    <Typography sx={{ fontWeight: 500, color: colors.textPrimary, fontSize: '0.85rem' }}>
                      {option.apiName}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem', fontFamily: 'monospace', ml: 0.5 }}>
                    {option.fullUrl || option.endpointPath}
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select API or enter URL..."
                onKeyDown={(e) => e.key === 'Enter' && !e.defaultPrevented && handleSend()}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary, fontFamily: 'monospace' },
                }}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  bgcolor: colors.surface,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 0,
                  '& .MuiAutocomplete-listbox': {
                    bgcolor: colors.surface,
                    '& .MuiAutocomplete-option': {
                      borderBottom: `1px solid ${colors.border}`,
                      '&:hover': { bgcolor: colors.surfaceElevated },
                      '&[aria-selected="true"]': { bgcolor: colors.surfaceElevated },
                    },
                  },
                },
              },
            }}
          />
          {/* Warehouse selector - for testing with different warehouse contexts */}
          {warehouses.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedWarehouse}
                onChange={(e) => setSelectedWarehouse(e.target.value)}
                displayEmpty
                sx={{
                  borderRadius: 0,
                  fontSize: '0.8rem',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                  '& .MuiSelect-select': { color: colors.textSecondary },
                }}
              >
                <MenuItem value="" disabled><em>Warehouse</em></MenuItem>
                {warehouses.map((wh) => (
                  <MenuItem key={wh.id} value={wh.id}>
                    {wh.systemName || wh.systemCode || wh.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Send button */}
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={tabState.isLoading || !tabState.url?.trim()}
            sx={{
              minWidth: 100,
              borderRadius: 0,
              bgcolor: colors.accent,
              '&:hover': { bgcolor: colors.accentDark },
              '&.Mui-disabled': { bgcolor: colors.surfaceElevated },
            }}
          >
            {tabState.isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          </Button>
        </Box>
      </Box>

      {/* Middle Section - Headers and Body side by side */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* Headers Panel */}
        <Box sx={{ width: '40%', borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>Headers</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={headerSelectValue}
                  onChange={(e) => handleAddHeader(e.target.value)}
                  displayEmpty
                  sx={{
                    borderRadius: 0,
                    fontSize: '0.7rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                    '& .MuiSelect-select': { color: colors.textSecondary, py: 0.25, px: 1 },
                  }}
                >
                  <MenuItem value="" disabled><em>Add...</em></MenuItem>
                  {COMMON_HEADERS.map((h, idx) => (
                    <MenuItem key={idx} value={h.value} sx={{ fontSize: '0.75rem' }}>{h.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Add common headers">
                <IconButton size="small" onClick={handleAddCommonHeaders} sx={{ color: colors.textMuted, p: 0.5 }}>
                  <PlaylistAddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear">
                <IconButton size="small" onClick={() => setTabState(prev => ({ ...prev, headers: '' }))} sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.error } }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {/* Auto-injected headers info for connected systems */}
          {tabState.isConnectedSystem && (
            <Box sx={{
              px: 1.5, py: 1,
              bgcolor: colors.infoBg || 'rgba(33, 150, 243, 0.1)',
              borderBottom: `1px solid ${colors.border}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}>
              <Typography variant="caption" sx={{ color: colors.info || '#2196f3', fontWeight: 500 }}>
                Auth headers auto-injected for {tabState.systemCode || 'connected system'}:
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace', fontSize: '0.7rem' }}>
                X-Fast-Api-Key: ****{'\n'}
                X-Tenant-Id: ****{'\n'}
                X-User-Id: ****{'\n'}
                X-Warehouse-Id: (selected above)
              </Typography>
            </Box>
          )}
          {/* Warning for protected headers */}
          {headerWarning && (
            <Box sx={{
              px: 1.5, py: 0.75,
              bgcolor: colors.warningBg || 'rgba(255, 152, 0, 0.1)',
              borderBottom: `1px solid ${colors.warning || '#ff9800'}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <Typography variant="caption" sx={{ color: colors.warning || '#ff9800', fontWeight: 500 }}>
                ⚠️ {headerWarning}
              </Typography>
            </Box>
          )}
          <TextField
            multiline fullWidth
            placeholder={tabState.isConnectedSystem
              ? "Add custom headers here (optional)..."
              : "Authorization: Bearer token\nContent-Type: application/json"}
            value={tabState.headers || ''}
            onChange={handleHeadersChange}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: 0, bgcolor: colors.surfaceElevated, height: '100%', alignItems: 'flex-start', '& fieldset': { border: 'none' } },
              '& .MuiInputBase-input': { color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.75rem', p: 1.5 },
            }}
          />
        </Box>

        {/* Body Panel */}
        <Box sx={{ width: '60%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 1.5, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2" sx={{ color: colors.textSecondary, fontSize: '0.8rem' }}>Request Body (JSON)</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {jsonValidation.message && (
                <Typography variant="caption" sx={{ color: jsonValidation.valid ? colors.success : colors.error, fontSize: '0.65rem', mr: 0.5 }}>
                  {jsonValidation.valid ? '✓' : '✗'} {jsonValidation.message}
                </Typography>
              )}
              <Tooltip title="Validate JSON">
                <IconButton size="small" onClick={handleValidateJson} sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.success } }}>
                  <CheckIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Format JSON">
                <IconButton size="small" onClick={handleFormatJson} sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.accent } }}>
                  <CodeIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Clear">
                <IconButton size="small" onClick={() => { setTabState(prev => ({ ...prev, body: '' })); setJsonValidation({ valid: null, message: '' }); }} sx={{ color: colors.textMuted, p: 0.5, '&:hover': { color: colors.error } }}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <TextField
            multiline fullWidth placeholder='{"key": "value"}'
            value={tabState.body || ''}
            onChange={(e) => {
              setTabState(prev => ({ ...prev, body: e.target.value }));
              setJsonValidation({ valid: null, message: '' });
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': { borderRadius: 0, bgcolor: colors.surfaceElevated, height: '100%', alignItems: 'flex-start', '& fieldset': { border: 'none' } },
              '& .MuiInputBase-input': { color: colors.textPrimary, fontFamily: 'monospace', fontSize: '0.8rem', p: 1.5 },
            }}
          />
        </Box>
      </Box>

      {/* Bottom Section - Response (full width) */}
      <Box sx={{ height: '40%', minHeight: 150, borderTop: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>Response</Typography>
          {tabState.response && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{
                color: tabState.response.status < 300 ? colors.success : tabState.response.status < 400 ? colors.warning : colors.error,
                fontWeight: 600,
              }}>
                {tabState.response.status} {tabState.response.statusText}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>{tabState.response.time}ms</Typography>
              <Tooltip title="Copy response">
                <IconButton size="small" onClick={handleCopyResponse} sx={{ color: colors.textSecondary, p: 0.5 }}>
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 1.5, bgcolor: colors.surfaceElevated }}>
          {tabState.isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress size={24} sx={{ color: colors.info, mr: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>Sending request...</Typography>
            </Box>
          ) : tabState.error ? (
            <Box sx={{ p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
              <Typography sx={{ color: colors.error, fontWeight: 500, fontSize: '0.85rem' }}>Error: {tabState.error}</Typography>
            </Box>
          ) : tabState.response ? (
            <Box component="pre" sx={{
              m: 0, p: 1.5,
              color: colors.textPrimary,
              bgcolor: colors.surface,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              borderRadius: 0,
              border: `1px solid ${colors.border}`,
              height: '100%',
              overflow: 'auto',
            }}>
              {tabState.response.body}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <ApiIcon sx={{ fontSize: 32, color: colors.textMuted, mr: 1.5 }} />
              <Typography sx={{ color: colors.textMuted }}>Enter a URL and send a request</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// Tool Tab Content Component
// ============================================================================
const ToolTabContent = ({ colors, tabState, setTabState }) => {
  const [tools, setTools] = useState([]);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState(null);
  const [paramValues, setParamValues] = useState({});
  const [connectedSystemId, setConnectedSystemId] = useState('foysonis'); // Default company ID

  // Filter state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    categories: [],
    domains: [],
    searchText: '',
  });
  const filterOpen = Boolean(filterAnchorEl);

  // Get unique categories and domains from tools
  const uniqueCategories = [...new Set(tools.map(t => t.category).filter(Boolean))];
  const uniqueDomains = [...new Set(tools.map(t => t.domain).filter(Boolean))];

  // Filter tools based on current filters
  const filteredTools = tools.filter(tool => {
    // Category filter
    if (filters.categories.length > 0 && !filters.categories.includes(tool.category)) {
      return false;
    }
    // Domain filter
    if (filters.domains.length > 0 && !filters.domains.includes(tool.domain)) {
      return false;
    }
    // Search text filter
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      const matchesName = tool.toolName?.toLowerCase().includes(searchLower);
      const matchesDesc = tool.description?.toLowerCase().includes(searchLower);
      const matchesKey = tool.toolKey?.toLowerCase().includes(searchLower);
      if (!matchesName && !matchesDesc && !matchesKey) {
        return false;
      }
    }
    return true;
  });

  // Check if any filters are active
  const hasActiveFilters = filters.categories.length > 0 || filters.domains.length > 0 || filters.searchText;

  // Toggle category filter
  const toggleCategoryFilter = (category) => {
    setFilters(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  // Toggle domain filter
  const toggleDomainFilter = (domain) => {
    setFilters(prev => ({
      ...prev,
      domains: prev.domains.includes(domain)
        ? prev.domains.filter(d => d !== domain)
        : [...prev.domains, domain]
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({ categories: [], domains: [], searchText: '' });
  };

  // Load tools on mount
  useEffect(() => {
    loadTools();
  }, []);

  const loadTools = async () => {
    setToolsLoading(true);
    try {
      console.log('Loading tools...');
      const data = await LlmService.getTools();
      console.log('Tools loaded:', data);
      setTools(data.tools || []);
    } catch (err) {
      console.error('Failed to load tools:', err);
      setTabState(prev => ({ ...prev, error: 'Failed to load tools: ' + err.message }));
    } finally {
      setToolsLoading(false);
    }
  };

  const handleToolSelect = (tool) => {
    setSelectedTool(tool);
    setSelectedFunction(null);
    setParamValues({});
    setTabState(prev => ({ ...prev, response: null, error: null }));
  };

  const handleFunctionSelect = (func) => {
    setSelectedFunction(func);
    // Initialize parameter values with defaults
    const params = func.parameters || {};
    const initialValues = {};
    Object.entries(params).forEach(([key, config]) => {
      if (config.default !== undefined) {
        initialValues[key] = config.default;
      } else {
        initialValues[key] = '';
      }
    });
    setParamValues(initialValues);
    setTabState(prev => ({ ...prev, response: null, error: null }));
  };

  const handleParamChange = (paramName, value) => {
    setParamValues(prev => ({ ...prev, [paramName]: value }));
  };

  const handleExecute = async () => {
    if (!selectedTool || !selectedFunction) {
      setTabState(prev => ({ ...prev, error: 'Please select a tool and function' }));
      return;
    }

    setTabState(prev => ({ ...prev, isLoading: true, error: null, response: null }));

    try {
      // Clean up parameters - remove empty strings
      const cleanParams = {};
      Object.entries(paramValues).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          cleanParams[key] = value;
        }
      });

      console.log('Executing tool function:', {
        toolId: selectedTool.toolId,
        functionName: selectedFunction.name,
        parameters: cleanParams,
      });

      const result = await LlmService.executeToolFunction(
        selectedTool.toolId,
        selectedFunction.name,
        cleanParams,
        connectedSystemId
      );

      console.log('Tool execution result:', result);

      setTabState(prev => ({ ...prev, response: result, isLoading: false }));
    } catch (err) {
      console.error('Tool execution error:', err);
      setTabState(prev => ({ ...prev, error: err.message || 'Execution failed', isLoading: false }));
    }
  };

  const handleClear = () => {
    setParamValues({});
    setTabState(prev => ({ ...prev, response: null, error: null }));
  };

  const handleCopyResponse = () => {
    if (tabState.response?.data) {
      navigator.clipboard.writeText(JSON.stringify(tabState.response.data, null, 2));
    }
  };

  return (
    <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Left Panel - Tool Selection */}
      <Box sx={{ width: '30%', borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tools Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>
            Tools {hasActiveFilters && `(${filteredTools.length}/${tools.length})`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Filter tools">
              <IconButton
                size="small"
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                sx={{
                  color: hasActiveFilters ? colors.success : colors.textMuted,
                  bgcolor: hasActiveFilters ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                  '&:hover': { bgcolor: hasActiveFilters ? 'rgba(34, 197, 94, 0.2)' : colors.surfaceElevated },
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh tools">
              <IconButton size="small" onClick={loadTools} disabled={toolsLoading} sx={{ color: colors.textMuted }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Filter Popover */}
        <Popover
          open={filterOpen}
          anchorEl={filterAnchorEl}
          onClose={() => setFilterAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0, p: 2, minWidth: 280 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>Filter Tools</Typography>
            {hasActiveFilters && (
              <Button size="small" onClick={clearFilters} sx={{ color: colors.error, fontSize: '0.7rem', p: 0.5 }}>
                Clear All
              </Button>
            )}
          </Box>

          {/* Search Text */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search tools..."
            value={filters.searchText}
            onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
              '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.875rem' },
            }}
          />

          {/* Category Filters */}
          {uniqueCategories.length > 0 && (
            <>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                Category
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {uniqueCategories.map(category => (
                  <Chip
                    key={category}
                    label={category}
                    size="small"
                    onClick={() => toggleCategoryFilter(category)}
                    sx={{
                      borderRadius: 0,
                      bgcolor: filters.categories.includes(category) ? colors.success : colors.surfaceElevated,
                      color: filters.categories.includes(category) ? '#fff' : colors.textSecondary,
                      '&:hover': { bgcolor: filters.categories.includes(category) ? colors.successDark : colors.border },
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </>
          )}

          {/* Domain Filters */}
          {uniqueDomains.length > 0 && (
            <>
              <Typography variant="caption" sx={{ color: colors.textSecondary, fontWeight: 600, display: 'block', mb: 1 }}>
                Domain
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {uniqueDomains.map(domain => (
                  <Chip
                    key={domain}
                    label={domain}
                    size="small"
                    onClick={() => toggleDomainFilter(domain)}
                    sx={{
                      borderRadius: 0,
                      bgcolor: filters.domains.includes(domain) ? colors.info : colors.surfaceElevated,
                      color: filters.domains.includes(domain) ? '#fff' : colors.textSecondary,
                      '&:hover': { bgcolor: filters.domains.includes(domain) ? colors.infoDark : colors.border },
                      fontSize: '0.7rem',
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Popover>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${colors.border}`, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {filters.searchText && (
              <Chip
                label={`"${filters.searchText}"`}
                size="small"
                onDelete={() => setFilters(prev => ({ ...prev, searchText: '' }))}
                sx={{ borderRadius: 0, bgcolor: colors.surfaceElevated, color: colors.textSecondary, fontSize: '0.65rem', height: 20 }}
              />
            )}
            {filters.categories.map(cat => (
              <Chip
                key={cat}
                label={cat}
                size="small"
                onDelete={() => toggleCategoryFilter(cat)}
                sx={{ borderRadius: 0, bgcolor: colors.successSubtle, color: colors.success, fontSize: '0.65rem', height: 20 }}
              />
            ))}
            {filters.domains.map(dom => (
              <Chip
                key={dom}
                label={dom}
                size="small"
                onDelete={() => toggleDomainFilter(dom)}
                sx={{ borderRadius: 0, bgcolor: colors.infoSubtle, color: colors.info, fontSize: '0.65rem', height: 20 }}
              />
            ))}
          </Box>
        )}

        {/* Tools List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {toolsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} sx={{ color: colors.success }} />
            </Box>
          ) : filteredTools.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {hasActiveFilters ? 'No tools match filters' : 'No tools available'}
              </Typography>
              {hasActiveFilters && (
                <Button size="small" onClick={clearFilters} sx={{ color: colors.success, mt: 1, fontSize: '0.75rem' }}>
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            filteredTools.map((tool) => (
              <Box
                key={tool.toolId}
                onClick={() => handleToolSelect(tool)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${colors.border}`,
                  bgcolor: selectedTool?.toolId === tool.toolId ? 'rgba(34, 197, 94, 0.1)' : 'transparent',
                  borderLeft: selectedTool?.toolId === tool.toolId ? `3px solid ${colors.success}` : '3px solid transparent',
                  '&:hover': { bgcolor: selectedTool?.toolId === tool.toolId ? 'rgba(34, 197, 94, 0.15)' : colors.surfaceElevated },
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                  {tool.toolName}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                  {tool.functionCount} function{tool.functionCount !== 1 ? 's' : ''} • {tool.category}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Middle Panel - Function Selection & Parameters */}
      <Box sx={{ width: '35%', borderRight: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {selectedTool ? (
          <>
            {/* Selected Tool Info */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surfaceElevated }}>
              <Typography variant="subtitle2" sx={{ color: colors.success, fontWeight: 600 }}>{selectedTool.toolName}</Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>{selectedTool.description}</Typography>
            </Box>

            {/* Company/Connected System ID */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
              <TextField
                label="System Code (connectedSystemId)"
                size="small"
                fullWidth
                value={connectedSystemId}
                onChange={(e) => setConnectedSystemId(e.target.value)}
                placeholder="e.g., foysonis"
                helperText="Maps to company ID in external system"
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                  '& .MuiInputBase-input': { color: colors.textPrimary },
                  '& .MuiInputLabel-root': { color: colors.textSecondary },
                  '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: '0.7rem' },
                }}
              />
            </Box>

            {/* Functions List */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}` }}>
              <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 1 }}>Select Function</Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={selectedFunction?.name || ''}
                  onChange={(e) => {
                    const func = selectedTool.functions.find(f => f.name === e.target.value);
                    handleFunctionSelect(func);
                  }}
                  displayEmpty
                  sx={{
                    borderRadius: 0,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                    '& .MuiSelect-select': { color: colors.textPrimary },
                  }}
                >
                  <MenuItem value="" disabled><em>Choose a function...</em></MenuItem>
                  {(selectedTool.functions || []).map((func) => (
                    <MenuItem key={func.name} value={func.name}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{func.name}</Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          {func.method} {func.api_endpoint}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Parameters */}
            {selectedFunction && (
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="subtitle2" sx={{ color: colors.textSecondary, mb: 2 }}>Parameters</Typography>

                {selectedFunction.parameters && Object.keys(selectedFunction.parameters).length > 0 ? (
                  Object.entries(selectedFunction.parameters).map(([paramName, paramConfig]) => (
                    <Box key={paramName} sx={{ mb: 2 }}>
                      <TextField
                        label={`${paramName}${paramConfig.required ? ' *' : ''}`}
                        size="small"
                        fullWidth
                        type={paramConfig.type === 'integer' || paramConfig.type === 'number' ? 'number' : 'text'}
                        value={paramValues[paramName] || ''}
                        onChange={(e) => handleParamChange(paramName, e.target.value)}
                        placeholder={paramConfig.default !== undefined ? `Default: ${paramConfig.default}` : ''}
                        helperText={paramConfig.description}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: 0, '& fieldset': { borderColor: colors.border } },
                          '& .MuiInputBase-input': { color: colors.textPrimary },
                          '& .MuiInputLabel-root': { color: colors.textSecondary },
                          '& .MuiFormHelperText-root': { color: colors.textMuted, fontSize: '0.7rem' },
                        }}
                      />
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: colors.textMuted, fontStyle: 'italic' }}>
                    No parameters required
                  </Typography>
                )}

                {/* Endpoint Info */}
                <Box sx={{ mt: 3, p: 1.5, bgcolor: colors.surface, border: `1px solid ${colors.border}` }}>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block' }}>
                    <strong>Method:</strong> <span style={{ color: selectedFunction.method === 'GET' ? colors.success : colors.warning }}>{selectedFunction.method}</span>
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', fontFamily: 'monospace' }}>
                    <strong>Endpoint:</strong> {selectedFunction.api_endpoint}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Action Buttons */}
            <Box sx={{ p: 2, borderTop: `1px solid ${colors.border}`, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleClear}
                sx={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: 0 }}>
                Clear
              </Button>
              <Button
                variant="contained"
                startIcon={tabState.isLoading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                onClick={handleExecute}
                disabled={tabState.isLoading || !selectedFunction}
                sx={{ bgcolor: colors.success, borderRadius: 0, '&:hover': { bgcolor: colors.successDark }, '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted } }}
              >
                {tabState.isLoading ? 'Executing...' : 'Execute'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <BuildIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography sx={{ color: colors.textMuted }}>Select a tool from the left panel</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Right Panel - Response */}
      <Box sx={{ width: '35%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ color: colors.textSecondary }}>Response</Typography>
          {tabState.response && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="caption" sx={{
                color: tabState.response.success ? colors.success : colors.error,
                fontWeight: 600,
              }}>
                {tabState.response.status} {tabState.response.statusText}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>{tabState.response.time}ms</Typography>
              <Tooltip title="Copy response">
                <IconButton size="small" onClick={handleCopyResponse} sx={{ color: colors.textSecondary }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: colors.surfaceElevated }}>
          {tabState.isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: colors.success, mb: 2 }} />
              <Typography sx={{ color: colors.textSecondary }}>Executing function...</Typography>
            </Box>
          ) : tabState.error ? (
            <Box sx={{ p: 2, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
              <Typography sx={{ color: colors.error, fontWeight: 500, mb: 1 }}>Error</Typography>
              <Typography sx={{ color: colors.textPrimary }}>{tabState.error}</Typography>
            </Box>
          ) : tabState.response ? (
            <Box>
              {/* Execution Details */}
              {tabState.response.success === false ? (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.errorSubtle, border: `1px solid ${colors.error}`, borderRadius: 0 }}>
                  <Typography variant="caption" sx={{ color: colors.error, display: 'block', fontWeight: 600 }}>
                    Execution Failed
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.error, display: 'block' }}>
                    {typeof tabState.response.error === 'object'
                      ? JSON.stringify(tabState.response.error)
                      : (tabState.response.error || tabState.response.errorType || 'Unknown error')}
                  </Typography>
                  {tabState.response.status && (
                    <Typography variant="caption" sx={{ color: colors.error, display: 'block' }}>
                      Status: {tabState.response.status} {tabState.response.statusText}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.successSubtle, border: `1px solid ${colors.success}`, borderRadius: 0 }}>
                  <Typography variant="caption" sx={{ color: colors.success, display: 'block' }}>
                    <strong>Status:</strong> {tabState.response.status} {tabState.response.statusText} ({tabState.response.time}ms)
                  </Typography>
                  {tabState.response.fullUrl && (
                    <Typography variant="caption" sx={{ color: colors.success, display: 'block', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      <strong>URL:</strong> {tabState.response.fullUrl}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Response Data */}
              <Box component="pre" sx={{
                m: 0, p: 2,
                color: colors.textPrimary,
                bgcolor: colors.surface,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                borderRadius: 0,
                border: `1px solid ${colors.border}`,
                minHeight: '100px',
                maxHeight: '500px',
                overflow: 'auto',
              }}>
                {JSON.stringify(tabState.response.data || tabState.response.error || tabState.response, null, 2)}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <BuildIcon sx={{ fontSize: 48, color: colors.textMuted, mb: 2 }} />
              <Typography sx={{ color: colors.textMuted }}>Select a tool and function, then click "Execute"</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ============================================================================
// Main Playground Component
// ============================================================================
const LlmPlayground = ({
  open,
  onClose,
  colors: externalColors,
  tabs: externalTabs,
  setTabs: externalSetTabs,
  activeTabId: externalActiveTabId,
  setActiveTabId: externalSetActiveTabId,
  standalone = false, // When true, renders without Dialog wrapper
  embedded = false, // When true, hides the left sidebar (for use inside FastStudioShell)
  initialTabType = TAB_TYPES.LLM, // Type of tab to create when initializing ('llm' or 'api')
}) => {
  // Use theme system for colors
  const { colors: themeColors } = useAppTheme();

  // Map theme colors to component format
  const defaultColors = {
    background: themeColors.background,
    surface: themeColors.surface,
    surfaceElevated: themeColors.surfaceElevated,
    surfaceHover: themeColors.surfaceHover,
    border: themeColors.border.default,
    borderLight: themeColors.border.light,
    textPrimary: themeColors.text.primary,
    textSecondary: themeColors.text.secondary,
    textMuted: themeColors.text.muted,
    // Primary/Accent colors (purple)
    accent: themeColors.primary.main,
    accentLight: themeColors.primary.light,
    accentDark: themeColors.primary.dark,
    accentSubtle: themeColors.primary.subtle || 'rgba(139, 92, 246, 0.15)',
    // Semantic colors with dark variants for hover states
    success: themeColors.success.main,
    successDark: themeColors.success.dark,
    successSubtle: themeColors.success.subtle,
    warning: themeColors.warning.main,
    warningDark: themeColors.warning.dark,
    warningSubtle: themeColors.warning.subtle,
    error: themeColors.error.main,
    errorDark: themeColors.error.dark,
    errorSubtle: themeColors.error.subtle,
    info: themeColors.info.main,
    infoDark: themeColors.info.dark,
    infoSubtle: themeColors.info.subtle,
    // Additional accent colors for sidebar icons
    cyan: themeColors.secondary?.main || '#06b6d4',
    cyanSubtle: themeColors.secondary?.subtle || 'rgba(6, 182, 212, 0.15)',
    pink: '#ec4899',
    pinkSubtle: 'rgba(236, 72, 153, 0.15)',
    // HTTP method colors (semantic mapping)
    httpGet: themeColors.success.main,
    httpGetBg: themeColors.success.subtle,
    httpPost: themeColors.warning.main,
    httpPostBg: themeColors.warning.subtle,
    httpPut: themeColors.info.main,
    httpPutBg: themeColors.info.subtle,
    httpPatch: themeColors.primary.main,
    httpPatchBg: themeColors.primary.subtle || 'rgba(139, 92, 246, 0.2)',
    httpDelete: themeColors.error.main,
    httpDeleteBg: themeColors.error.subtle,
  };
  const colors = externalColors || defaultColors;
  // Use external state if provided, otherwise use internal state
  const [internalTabs, setInternalTabs] = useState([]);
  const [internalActiveTabId, setInternalActiveTabId] = useState(null);

  // Unsaved changes confirmation dialog state
  const [closeConfirmDialog, setCloseConfirmDialog] = useState({ open: false, tabId: null });

  const tabs = externalTabs !== undefined ? externalTabs : internalTabs;
  const setTabs = externalSetTabs !== undefined ? externalSetTabs : setInternalTabs;
  const activeTabId = externalActiveTabId !== undefined ? externalActiveTabId : internalActiveTabId;
  const setActiveTabId = externalSetActiveTabId !== undefined ? externalSetActiveTabId : setInternalActiveTabId;

  // Check if a tab has unsaved changes
  const hasUnsavedChanges = (tab) => {
    if (!tab) return false;
    if (tab.type === TAB_TYPES.LLM) {
      return !!(tab.state.systemPrompt?.trim() || tab.state.userPrompt?.trim());
    } else if (tab.type === TAB_TYPES.API) {
      return !!(tab.state.url?.trim() || tab.state.headers?.trim() || tab.state.body?.trim());
    }
    return false;
  };

  // Initialize with one tab when opened and no tabs exist
  useEffect(() => {
    if ((open || standalone) && tabs.length === 0) {
      const newTab = createTabHelper(initialTabType, []);
      setTabs([newTab]);
      setActiveTabId(newTab.id);
    }
  }, [open, standalone, tabs.length, initialTabType]);

  const createTabHelper = (type, currentTabs) => {
    const id = generateTabId();
    const tabNumber = currentTabs.filter(t => t.type === type).length + 1;

    if (type === TAB_TYPES.LLM) {
      return {
        id,
        type,
        title: `LLM ${tabNumber}`,
        state: {
          systemPrompt: '',
          userPrompt: '',
          selectedModelId: '',
          temperature: 0.7,
          maxTokens: 2048,
          simulateUserId: '1',
          simulateWarehouseId: '',
          response: null,
          isLoading: false,
          error: null,
        },
      };
    } else if (type === TAB_TYPES.API) {
      return {
        id,
        type,
        title: `API Hound ${tabNumber}`,
        state: {
          method: 'GET',
          url: '',
          headers: '',
          body: '',
          response: null,
          isLoading: false,
          error: null,
        },
      };
    } else if (type === TAB_TYPES.TOOL) {
      return {
        id,
        type,
        title: `Tool ${tabNumber}`,
        state: {
          response: null,
          isLoading: false,
          error: null,
        },
      };
    }
  };

  const handleAddTab = (type) => {
    const newTab = createTabHelper(type, tabs);
    setTabs([...tabs, newTab]);
    setActiveTabId(newTab.id);
  };

  const handleCloseTab = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);

    // Check for unsaved changes
    if (hasUnsavedChanges(tab)) {
      setCloseConfirmDialog({ open: true, tabId });
      return;
    }

    // No unsaved changes, close directly
    closeTabDirectly(tabId);
  };

  // Actually close the tab (called after confirmation or when no unsaved changes)
  const closeTabDirectly = (tabId) => {
    const tabIndex = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);

    if (newTabs.length === 0) {
      // If closing last tab, create a new LLM tab
      const newTab = createTabHelper(TAB_TYPES.LLM, []);
      setTabs([newTab]);
      setActiveTabId(newTab.id);
    } else {
      setTabs(newTabs);
      if (activeTabId === tabId) {
        // Switch to adjacent tab
        const newIndex = Math.min(tabIndex, newTabs.length - 1);
        setActiveTabId(newTabs[newIndex].id);
      }
    }
    setCloseConfirmDialog({ open: false, tabId: null });
  };

  // Handle confirmation dialog actions
  const handleCloseConfirmDiscard = () => {
    if (closeConfirmDialog.tabId) {
      closeTabDirectly(closeConfirmDialog.tabId);
    }
  };

  const handleCloseConfirmCancel = () => {
    setCloseConfirmDialog({ open: false, tabId: null });
  };

  const updateTabState = (tabId, newState) => {
    const updatedTabs = tabs.map(tab =>
      tab.id === tabId ? { ...tab, state: typeof newState === 'function' ? newState(tab.state) : { ...tab.state, ...newState } } : tab
    );
    setTabs(updatedTabs);
  };

  const activeTab = tabs.find(t => t.id === activeTabId);

  // Handle close/navigate home
  const handleClose = () => {
    if (standalone) {
      window.location.href = '/';
    } else if (onClose) {
      onClose();
    }
  };

  // Inner content (shared between standalone and dialog modes)
  const playgroundContent = (
    <Box sx={{ display: 'flex', flex: 1, height: '100%' }}>
      {/* Left Icon Sidebar - hidden when embedded in FastStudioShell */}
      {!embedded && (
        <Box
          sx={{
            width: 48,
            bgcolor: colors.surface,
            borderRight: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 1,
            gap: 0.5,
          }}
        >
          {standalone && (
            <Tooltip title="Back to Main App" placement="right">
              <IconButton
                onClick={handleClose}
                sx={{
                  color: colors.textSecondary,
                  borderRadius: 0,
                  mb: 1,
                  '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)', color: colors.textPrimary },
                }}
              >
                <HomeIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="New LLM Tab" placement="right">
            <IconButton
              onClick={() => handleAddTab(TAB_TYPES.LLM)}
              sx={{
                color: colors.accent,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.accentSubtle },
              }}
            >
              <PsychologyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="New API Hound" placement="right">
            <IconButton
              onClick={() => handleAddTab(TAB_TYPES.API)}
              sx={{
                color: colors.info,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.infoSubtle },
              }}
            >
              <ApiIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="New Tool Tab" placement="right">
            <IconButton
              onClick={() => handleAddTab(TAB_TYPES.TOOL)}
              sx={{
                color: colors.success,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.successSubtle },
              }}
            >
              <BuildIcon />
            </IconButton>
          </Tooltip>

          {/* Divider */}
          <Box sx={{ my: 1, borderTop: `1px solid ${colors.border}` }} />

          <Tooltip title="FLEX IDE (Scripting)" placement="right">
            <IconButton
              onClick={() => window.open('/flex-ide', '_blank')}
              sx={{
                color: colors.warning,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.warningSubtle },
              }}
            >
              <CodeIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Registry Manager" placement="right">
            <IconButton
              onClick={() => window.open('/registry-manager', '_blank')}
              sx={{
                color: colors.cyan,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.cyanSubtle },
              }}
            >
              <StorageIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Task Monitor" placement="right">
            <IconButton
              onClick={() => window.open('/task-monitor', '_blank')}
              sx={{
                color: colors.pink,
                borderRadius: 0,
                '&:hover': { bgcolor: colors.pinkSubtle },
              }}
            >
              <AssignmentIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tab Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: colors.surface,
            borderBottom: `1px solid ${colors.border}`,
            minHeight: 40,
          }}
        >
          {/* Add Tab Button when embedded */}
          {embedded && (
            <Tooltip title={initialTabType === TAB_TYPES.API ? 'New API Tab' : 'New LLM Tab'}>
              <IconButton
                onClick={() => handleAddTab(initialTabType)}
                sx={{
                  mx: 1,
                  color: initialTabType === TAB_TYPES.API ? colors.info : colors.accent,
                  '&:hover': { bgcolor: initialTabType === TAB_TYPES.API ? colors.infoSubtle : colors.accentSubtle },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
          {/* FAST STUDIO label when standalone but not embedded */}
          {standalone && !embedded && (
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, borderRight: `1px solid ${colors.border}` }}>
              <PsychologyIcon sx={{ color: colors.accent, mr: 1 }} />
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                FAST STUDIO
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'auto' }}>
            {tabs.map((tab) => (
              <Box
                key={tab.id}
                onClick={() => setActiveTabId(tab.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  bgcolor: tab.id === activeTabId ? colors.background : 'transparent',
                  borderRight: `1px solid ${colors.border}`,
                  borderBottom: tab.id === activeTabId ? `2px solid ${tab.type === TAB_TYPES.LLM ? colors.accent : tab.type === TAB_TYPES.API ? colors.info : colors.success}` : '2px solid transparent',
                  '&:hover': { bgcolor: tab.id === activeTabId ? colors.background : colors.surfaceElevated },
                }}
              >
                {tab.type === TAB_TYPES.LLM ? (
                  <PsychologyIcon sx={{ fontSize: 16, color: colors.accent }} />
                ) : tab.type === TAB_TYPES.API ? (
                  <ApiIcon sx={{ fontSize: 16, color: colors.info }} />
                ) : (
                  <BuildIcon sx={{ fontSize: 16, color: colors.success }} />
                )}
                <Typography variant="caption" sx={{ color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                  {tab.title}
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleCloseTab(tab.id); }}
                  sx={{ p: 0.25, color: colors.textMuted, '&:hover': { color: colors.textPrimary } }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
          {!standalone && (
            <IconButton onClick={handleClose} sx={{ color: colors.textSecondary, mr: 1 }}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {/* Tab Content - Render all tabs but only show active one to preserve state */}
        <Box sx={{ p: 0, display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
          {tabs.map((tab) => (
            <Box
              key={tab.id}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: tab.id === activeTabId ? 'flex' : 'none',
                overflow: 'hidden',
              }}
            >
              {tab.type === TAB_TYPES.LLM && (
                <LlmTabContent
                  colors={colors}
                  tabState={tab.state}
                  setTabState={(newState) => updateTabState(tab.id, newState)}
                />
              )}
              {tab.type === TAB_TYPES.API && (
                <ApiTabContent
                  colors={colors}
                  tabState={tab.state}
                  setTabState={(newState) => updateTabState(tab.id, newState)}
                />
              )}
              {tab.type === TAB_TYPES.TOOL && (
                <ToolTabContent
                  colors={colors}
                  tabState={tab.state}
                  setTabState={(newState) => updateTabState(tab.id, newState)}
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );

  // Unsaved changes confirmation dialog
  const unsavedChangesDialog = (
    <Dialog
      open={closeConfirmDialog.open}
      onClose={handleCloseConfirmCancel}
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 0,
          minWidth: 400,
        },
      }}
    >
      <DialogTitle sx={{ color: colors.textPrimary, borderBottom: `1px solid ${colors.border}`, pb: 1.5 }}>
        Unsaved Changes
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Typography sx={{ color: colors.textSecondary }}>
          This tab has unsaved content. Are you sure you want to close it?
        </Typography>
        <Typography variant="body2" sx={{ color: colors.textMuted, mt: 1 }}>
          Your changes will be lost if you don't save them.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}`, gap: 1 }}>
        <Button
          onClick={handleCloseConfirmCancel}
          sx={{ color: colors.textSecondary, borderRadius: 0 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCloseConfirmDiscard}
          sx={{ bgcolor: colors.error, borderRadius: 0, '&:hover': { bgcolor: colors.errorDark || colors.error } }}
        >
          Discard & Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Standalone mode: render directly without Dialog
  if (standalone) {
    return (
      <Box sx={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: colors.background }}>
        {playgroundContent}
        {unsavedChangesDialog}
      </Box>
    );
  }

  // Dialog mode: wrap in Dialog
  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: colors.background,
            borderRadius: 0,
            resize: 'both',
            overflow: 'auto',
            width: '95vw',
            height: '95vh',
            minWidth: '600px',
            minHeight: '400px',
            maxWidth: '98vw',
            maxHeight: '98vh',
            m: 1,
          },
        }}
      >
        {playgroundContent}
      </Dialog>
      {unsavedChangesDialog}
    </>
  );
};

export default LlmPlayground;
